import { useState, useEffect, useCallback, memo, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDistanceToNow, format, differenceInHours } from 'date-fns'
import { PageLayout } from './layout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { SelectTriggerMinimal } from '@/components/ui/select-minimal'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import ToggleSwitch from './ToggleSwitch'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import { CompactField } from '@/components/ui/compact-field'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command'
import { ChevronFirst, ChevronLeft, ChevronRight, ChevronLast, Plus, User, Building2, ArrowRight, Check, MapPin, Phone, Mail, Store, Users, ShoppingCart, UtensilsCrossed, Truck, Edit, Trash2, Save, X, AlertTriangle, Coffee, CakeSlice, Package, ChevronsUpDown, ChevronDown, ChevronUp, Loader2, Building, ShoppingBag, Croissant } from 'lucide-react'
import {
  supabase,
  createCustomer,
  updateCustomer,
  deleteCustomer,
  createCustomerBranch,
  updateCustomerBranch,
  deleteCustomerBranch,
  createProductType,
  createProduct,
  createImportSession,
  updateImportSession,
  fetchImportSession,
  fetchUserImportSessions,
  createStagingSalesRecords,
  commitStagingImportToSales,
  fetchImportSessionsForReimport,
  reImportFromSession
} from '../supabase-client'
import { useAutoSave } from '../hooks/useAutoSave'
import { useDataCache } from '../contexts/DataCacheContext'
import { loadSessionState, isValidSessionState, getSessionStateSummary } from '../utils/sessionStateManager'
import { findMatchingTemplate, createTemplate, updateTemplateLastUsed, fetchUserTemplates, deleteTemplate, renameTemplate } from '../utils/templateManager'
import { getDuplicateDetectionSummary, saveImportHistory } from '../utils/recordFingerprintManager'
import { retryDatabaseQuery } from '../utils/retry'
import ImportWizardV2PreviewModal from './ImportWizardV2PreviewModal'
import ReviewViewPrototype from './import-wizard/ReviewViewPrototype'
import ProductTypeMatchModal from './import-wizard/ProductTypeMatchModal'
import ProductStrainMatchModal from './import-wizard/ProductStrainMatchModal'
import { groupProductVariants, normalizeGroupForDisplay } from '../utils/productVariantGrouping'
import ImportHistoryDetailView from './ImportHistoryDetailView'

// Branch type icons
const branchTypeIcons = {
  company: Building,
  branch: Store,
  retail: ShoppingBag,
  grocer: ShoppingCart,
  restaurant: UtensilsCrossed,
  distributor: Truck,
  cafe: Coffee,
  bakery: Croissant
}

const getBranchTypeIcon = (branchType) => {
  const IconComponent = branchTypeIcons[branchType] || Store
  return <IconComponent className="size-4 inline mr-1" />
}

// Optimized modal form component that manages its own state
const CustomerModalForm = memo(({ csvValue, onSubmit, onCancel }) => {
  // Local state for this modal only - doesn't trigger parent re-renders
  const [localFormData, setLocalFormData] = useState({
    customerName: csvValue || '',
    customerType: 'grocer' // Default to grocer (most common type)
  })
  const [isLoading, setIsLoading] = useState(false)
  const [validationErrors, setValidationErrors] = useState({})

  const handleFieldChange = useCallback((field, value) => {
    setLocalFormData(prev => ({ ...prev, [field]: value }))
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: null }))
    }
  }, [validationErrors])

  const handleSubmit = async () => {
    // Validate fields
    const errors = {}
    if (!localFormData.customerName?.trim()) {
      errors.customerName = 'Customer name is required'
    }
    if (!localFormData.customerType?.trim()) {
      errors.customerType = 'Customer type is required'
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      return
    }

    setIsLoading(true)
    try {
      await onSubmit(localFormData)
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const customerTypeOptions = [
    { value: 'grocer', label: 'Grocer', icon: ShoppingCart },
    { value: 'restaurant', label: 'Restaurant', icon: UtensilsCrossed },
    { value: 'cafe', label: 'Cafe', icon: Coffee },
    { value: 'bakery', label: 'Bakery', icon: CakeSlice },
    { value: 'direct', label: 'Direct', icon: Users },
    { value: 'market', label: 'Market', icon: Store },
    { value: 'vegbox', label: 'VegBox', icon: Package },
    { value: 'distributor', label: 'Distributor', icon: Truck }
  ]

  return (
    <>
      <DialogHeader className="border-b pb-4">
        <DialogTitle className="text-xl font-semibold">Add New Customer</DialogTitle>
        <DialogDescription className="text-base">
          Creating customer from:{' '}
          <span className="font-medium text-gray-900 bg-gray-100 px-2 py-1 rounded">
            "{csvValue}"
          </span>
        </DialogDescription>
      </DialogHeader>

      <div className="py-6 space-y-6">
        {/* Customer Name Field */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-gray-500" />
            <Label className="font-medium text-gray-700">Customer Name *</Label>
          </div>
          <div className="relative">
            <Input
              value={localFormData.customerName}
              onChange={(e) => handleFieldChange('customerName', e.target.value)}
              placeholder="Enter customer name"
              className={`text-lg px-4 py-3 transition-all duration-200 ${
                validationErrors.customerName
                  ? 'border-red-300 focus:border-red-500'
                  : 'focus:bg-gray-50 focus:border-gray-400'
              }`}
            />
            {localFormData.customerName && !validationErrors.customerName && (
              <Check className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
            )}
          </div>
          {validationErrors.customerName && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <span className="w-4 h-4">⚠️</span>
              {validationErrors.customerName}
            </p>
          )}
          {!validationErrors.customerName && localFormData.customerName && (
            <p className="text-sm text-gray-500">This will be the customer's display name</p>
          )}
        </div>

        {/* Customer Type Field */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-gray-500" />
            <Label className="font-medium text-gray-700">Customer Type *</Label>
          </div>
          <Select
            value={localFormData.customerType}
            onValueChange={(value) => handleFieldChange('customerType', value)}
          >
            <SelectTrigger className={`px-4 py-3 text-base transition-all duration-200 ${
              validationErrors.customerType
                ? 'border-red-300 focus:border-red-500'
                : 'hover:border-gray-400'
            }`}>
              <SelectValue placeholder="Select customer type" />
            </SelectTrigger>
            <SelectContent>
              {customerTypeOptions.map((option) => {
                const IconComponent = option.icon
                return (
                  <SelectItem key={option.value} value={option.value} className="flex items-center gap-2">
                    <IconComponent className="w-4 h-4 mr-2" />
                    {option.label}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
          {validationErrors.customerType && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <span className="w-4 h-4">⚠️</span>
              {validationErrors.customerType}
            </p>
          )}
        </div>
      </div>

      <DialogFooter className="border-t pt-4 flex justify-end gap-3">
        <Button
          variant="ghost"
          onClick={onCancel}
          disabled={isLoading}
          className="px-6"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isLoading}
          className="px-6 bg-green-600 hover:bg-green-700 text-white shadow-sm hover:shadow-md transition-all duration-200"
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Creating...
            </div>
          ) : (
            <div className="flex items-center gap-2">
              Continue
              <ArrowRight className="w-4 h-4" />
            </div>
          )}
        </Button>
      </DialogFooter>
    </>
  )
})

CustomerModalForm.displayName = 'CustomerModalForm'

// Optimized branch modal form component with enhanced design
const BranchModalForm = memo(({ csvValue, customerId, onSubmit, onCancel }) => {
  const [localFormData, setLocalFormData] = useState({
    branchName: csvValue || '',
    address: '',
    phone: '',
    email: ''
  })
  const [isLoading, setIsLoading] = useState(false)
  const [validationErrors, setValidationErrors] = useState({})

  const handleFieldChange = useCallback((field, value) => {
    setLocalFormData(prev => ({ ...prev, [field]: value }))
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: null }))
    }
  }, [validationErrors])

  const handleSubmit = async () => {
    // Validate required fields
    const errors = {}
    if (!localFormData.branchName?.trim()) {
      errors.branchName = 'Branch name is required'
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors)
      return
    }

    setIsLoading(true)
    try {
      await onSubmit({ ...localFormData, customerId })
    } catch (error) {
      console.error('Error submitting form:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <DialogHeader className="border-b pb-4">
        <DialogTitle className="text-xl font-semibold">Add New Branch</DialogTitle>
        <DialogDescription className="text-base">
          Creating branch from:{' '}
          <span className="font-medium text-gray-900 bg-gray-100 px-2 py-1 rounded">
            "{csvValue}"
          </span>
        </DialogDescription>
      </DialogHeader>

      <div className="py-6 space-y-6">
        {/* Branch Name Field */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-gray-500" />
            <Label className="font-medium text-gray-700">Branch Name *</Label>
          </div>
          <div className="relative">
            <Input
              value={localFormData.branchName}
              onChange={(e) => handleFieldChange('branchName', e.target.value)}
              placeholder="e.g., Main Store, West Location"
              className={`text-lg px-4 py-3 transition-all duration-200 ${
                validationErrors.branchName
                  ? 'border-red-300 focus:border-red-500'
                  : 'focus:bg-gray-50 focus:border-gray-400'
              }`}
            />
            {localFormData.branchName && !validationErrors.branchName && (
              <Check className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-500" />
            )}
          </div>
          {validationErrors.branchName && (
            <p className="text-sm text-red-600 flex items-center gap-1">
              <span className="w-4 h-4">⚠️</span>
              {validationErrors.branchName}
            </p>
          )}
          {!validationErrors.branchName && localFormData.branchName && (
            <p className="text-sm text-gray-500">This will identify the branch location</p>
          )}
        </div>

        {/* Address Field */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-gray-500" />
            <Label className="font-medium text-gray-700">Address</Label>
          </div>
          <Input
            value={localFormData.address}
            onChange={(e) => handleFieldChange('address', e.target.value)}
            placeholder="Branch address (optional)"
            className="px-4 py-3 text-base transition-all duration-200 focus:bg-gray-50 focus:border-gray-400"
          />
          <p className="text-sm text-gray-500">Optional: Physical location address</p>
        </div>

        {/* Contact Information Row */}
        <div className="grid grid-cols-2 gap-4">
          {/* Phone Field */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-gray-500" />
              <Label className="font-medium text-gray-700">Phone</Label>
            </div>
            <Input
              type="tel"
              value={localFormData.phone}
              onChange={(e) => handleFieldChange('phone', e.target.value)}
              placeholder="+44 123 456 7890"
              className="px-4 py-3 text-base transition-all duration-200 focus:bg-gray-50 focus:border-gray-400"
            />
          </div>

          {/* Email Field */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-gray-500" />
              <Label className="font-medium text-gray-700">Email</Label>
            </div>
            <Input
              type="email"
              value={localFormData.email}
              onChange={(e) => handleFieldChange('email', e.target.value)}
              placeholder="branch@company.com"
              className="px-4 py-3 text-base transition-all duration-200 focus:bg-gray-50 focus:border-gray-400"
            />
          </div>
        </div>

        <div className="text-sm text-gray-500 bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-start gap-2">
            <span className="text-blue-600 mt-0.5">💡</span>
            <div>
              <strong>Tip:</strong> Only branch name is required. Contact details can be added later if needed.
            </div>
          </div>
        </div>
      </div>

      <DialogFooter className="border-t pt-4 flex justify-end gap-3">
        <Button
          variant="ghost"
          onClick={onCancel}
          disabled={isLoading}
          className="px-6"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={isLoading}
          className="px-6 bg-green-600 hover:bg-green-700 text-white shadow-sm hover:shadow-md transition-all duration-200"
        >
          {isLoading ? (
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Creating...
            </div>
          ) : (
            <div className="flex items-center gap-2">
              Continue
              <ArrowRight className="w-4 h-4" />
            </div>
          )}
        </Button>
      </DialogFooter>
    </>
  )
})

BranchModalForm.displayName = 'BranchModalForm'

// CustomerEditModal component for CRUD operations on customers
const CustomerEditModal = memo(({
  customer,
  isOpen,
  onClose,
  onUpdate,
  onDelete,
  onSave,
  hasMatchedRecords
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    name: '',
    type: 'market',
    email: '',
    phone: '',
    paymentTerms: '30'
  })
  const [validationErrors, setValidationErrors] = useState({})

  // Branch editing state
  const [editingBranchId, setEditingBranchId] = useState(null)
  const [branchFormData, setBranchFormData] = useState({})
  const [showBranchDeleteConfirm, setShowBranchDeleteConfirm] = useState(false)
  const [deletingBranchId, setDeletingBranchId] = useState(null)

  // Customer type options with icons
  const customerTypeOptions = [
    { value: 'grocer', label: 'Grocer', icon: ShoppingCart },
    { value: 'restaurant', label: 'Restaurant', icon: UtensilsCrossed },
    { value: 'cafe', label: 'Cafe', icon: Coffee },
    { value: 'bakery', label: 'Bakery', icon: CakeSlice },
    { value: 'direct', label: 'Direct', icon: Users },
    { value: 'market', label: 'Market', icon: Store },
    { value: 'vegbox', label: 'VegBox', icon: Package },
    { value: 'distributor', label: 'Distributor', icon: Truck }
  ]

  // Initialize form data when customer changes
  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name || '',
        type: customer.branches?.[0]?.customer_type?.name || 'market',
        email: customer.email || '',
        phone: customer.phone || '',
        paymentTerms: customer.payment_terms || '30'
      })
    }
    setIsEditing(false)
    setValidationErrors({})
  }, [customer])

  const handleFieldChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: null }))
    }
  }, [validationErrors])

  const validateForm = () => {
    const errors = {}
    if (!formData.name?.trim()) {
      errors.name = 'Customer name is required'
    }
    if (!formData.email?.trim()) {
      errors.email = 'Email is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Please enter a valid email address'
    }
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSave = async () => {
    if (!validateForm()) return

    setIsLoading(true)
    try {
      const updateData = {
        companyName: formData.name,
        customerType: formData.type,
        email: formData.email,
        phone: formData.phone,
        paymentTerms: formData.paymentTerms
      }

      await onUpdate(customer.id, updateData)
      setIsEditing(false)

      // Call onSave callback to refresh data in parent
      if (onSave) {
        await onSave()
      }
    } catch (error) {
      console.error('Error updating customer:', error)
      setValidationErrors({ submit: 'Failed to update customer. Please try again.' })
    } finally {
      setIsLoading(false)
    }
  }

  const handleCancel = () => {
    // Reset form data to original values
    if (customer) {
      setFormData({
        name: customer.name || '',
        type: customer.branches?.[0]?.customer_type?.name || 'market',
        email: customer.email || '',
        phone: customer.phone || '',
        paymentTerms: customer.payment_terms || '30'
      })
    }
    setValidationErrors({})
    setIsEditing(false)
  }

  // Branch handlers
  const handleEditBranch = (branch) => {
    setEditingBranchId(branch.id)
    setBranchFormData({
      name: branch.name || '',
      address: branch.address || '',
      phone: branch.phone || '',
      email: branch.email || ''
    })
  }

  const handleSaveBranch = async (branchId) => {
    try {
      await updateCustomerBranch(branchId, branchFormData)

      // Refresh customer data by calling onSave callback
      if (onSave) {
        await onSave()
      }

      setEditingBranchId(null)
      setBranchFormData({})
    } catch (error) {
      console.error('Failed to update branch:', error)
      setValidationErrors({ submit: 'Failed to update branch. Please try again.' })
    }
  }

  const handleCancelBranchEdit = () => {
    setEditingBranchId(null)
    setBranchFormData({})
  }

  const handleDeleteBranch = async (branchId) => {
    try {
      await deleteCustomerBranch(branchId)

      // Refresh customer data by calling onSave callback
      if (onSave) {
        await onSave()
      }

      setShowBranchDeleteConfirm(false)
      setDeletingBranchId(null)
    } catch (error) {
      console.error('Failed to delete branch:', error)
      setValidationErrors({ submit: 'Failed to delete branch. Please try again.' })
    }
  }

  const handleBranchFieldChange = (field, value) => {
    setBranchFormData(prev => ({ ...prev, [field]: value }))
  }

  if (!customer) return null

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="flex items-center justify-between text-xl font-semibold">
            <span className="flex items-center gap-2">
              <User className="w-5 h-5 text-gray-500" />
              {customer.name}
            </span>
            <div className="flex items-center gap-2">
              {!isEditing ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  className="flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit
                </Button>
              ) : (
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancel}
                    disabled={isLoading}
                    className="flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={isLoading}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                  >
                    <Save className="w-4 h-4" />
                    Save
                  </Button>
                </div>
              )}
              <Button
                variant="destructive"
                size="sm"
                onClick={() => onDelete && onDelete(customer.id, hasMatchedRecords)}
                className="flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="py-6 space-y-6">
          {/* Company Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-gray-500" />
                Company Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Customer Name */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 font-medium text-gray-700">
                  <User className="w-4 h-4" />
                  Customer Name *
                </Label>
                {isEditing ? (
                  <Input
                    value={formData.name}
                    onChange={(e) => handleFieldChange('name', e.target.value)}
                    placeholder="Enter customer name"
                    className={`transition-all duration-200 ${
                      validationErrors.name ? 'border-red-300' : 'focus:border-gray-400'
                    }`}
                  />
                ) : (
                  <div className="p-3 bg-gray-50 rounded-md text-gray-900 font-medium">
                    {customer.name}
                  </div>
                )}
                {validationErrors.name && (
                  <p className="text-sm text-red-600">{validationErrors.name}</p>
                )}
              </div>

              {/* Customer Type */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 font-medium text-gray-700">
                  <Building2 className="w-4 h-4" />
                  Customer Type
                </Label>
                {isEditing ? (
                  <Select
                    value={formData.type}
                    onValueChange={(value) => handleFieldChange('type', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {customerTypeOptions.map((option) => {
                        const IconComponent = option.icon
                        return (
                          <SelectItem key={option.value} value={option.value}>
                            <div className="flex items-center gap-2">
                              <IconComponent className="w-4 h-4" />
                              {option.label}
                            </div>
                          </SelectItem>
                        )
                      })}
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="p-3 bg-gray-50 rounded-md">
                    <div className="flex items-center gap-2">
                      {(() => {
                        const customerType = customer.branches?.[0]?.customer_type?.name
                        const typeOption = customerTypeOptions.find(opt => opt.value === customerType)
                        const IconComponent = typeOption?.icon || Building2
                        return (
                          <>
                            <IconComponent className="w-4 h-4 text-gray-500" />
                            <span className="font-medium">{typeOption?.label || customerType || 'N/A'}</span>
                          </>
                        )
                      })()}
                    </div>
                  </div>
                )}
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 font-medium text-gray-700">
                  <Mail className="w-4 h-4" />
                  Email Address *
                </Label>
                {isEditing ? (
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleFieldChange('email', e.target.value)}
                    placeholder="customer@example.com"
                    className={`transition-all duration-200 ${
                      validationErrors.email ? 'border-red-300' : 'focus:border-gray-400'
                    }`}
                  />
                ) : (
                  <div className="p-3 bg-gray-50 rounded-md">
                    <a href={`mailto:${customer.email}`} className="text-blue-600 hover:text-blue-800">
                      {customer.email}
                    </a>
                  </div>
                )}
                {validationErrors.email && (
                  <p className="text-sm text-red-600">{validationErrors.email}</p>
                )}
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2 font-medium text-gray-700">
                  <Phone className="w-4 h-4" />
                  Phone Number
                </Label>
                {isEditing ? (
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleFieldChange('phone', e.target.value)}
                    placeholder="+44 20 1234 5678"
                  />
                ) : (
                  <div className="p-3 bg-gray-50 rounded-md">
                    {customer.phone ? (
                      <a href={`tel:${customer.phone}`} className="text-blue-600 hover:text-blue-800">
                        {customer.phone}
                      </a>
                    ) : (
                      <span className="text-gray-500 italic">No phone number</span>
                    )}
                  </div>
                )}
              </div>

              {/* Payment Terms */}
              <div className="space-y-2">
                <Label className="font-medium text-gray-700">Payment Terms</Label>
                {isEditing ? (
                  <Select
                    value={formData.paymentTerms}
                    onValueChange={(value) => handleFieldChange('paymentTerms', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="21">21 days</SelectItem>
                      <SelectItem value="28">28 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <div className="p-3 bg-gray-50 rounded-md">
                    {customer.payment_terms || '30'} days
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Branch Information */}
          {customer.branches && customer.branches.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-gray-500" />
                  Branch Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {customer.branches.filter(branch => branch.branch_type !== 'company').map((branch) => (
                    <div key={branch.id} className="p-4 bg-gray-50 rounded-md">
                      {editingBranchId === branch.id ? (
                        // Edit mode
                        <div className="space-y-4">
                          <div className="flex items-center justify-between mb-4">
                            <h4 className="font-medium text-gray-900">Edit Branch</h4>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleCancelBranchEdit}
                                className="h-8 w-8 p-0"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleSaveBranch(branch.id)}
                                className="h-8 w-8 p-0 text-green-600 hover:text-green-800"
                              >
                                <Save className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>

                          <div className="space-y-3">
                            <div>
                              <Label className="text-sm font-medium">Branch Name</Label>
                              <Input
                                value={branchFormData.name || ''}
                                onChange={(e) => handleBranchFieldChange('name', e.target.value)}
                                className="mt-1"
                              />
                            </div>

                            <div>
                              <Label className="text-sm font-medium">Address</Label>
                              <Input
                                value={branchFormData.address || ''}
                                onChange={(e) => handleBranchFieldChange('address', e.target.value)}
                                className="mt-1"
                                placeholder="Optional"
                              />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <Label className="text-sm font-medium">Phone</Label>
                                <Input
                                  value={branchFormData.phone || ''}
                                  onChange={(e) => handleBranchFieldChange('phone', e.target.value)}
                                  className="mt-1"
                                  placeholder="Optional"
                                />
                              </div>
                              <div>
                                <Label className="text-sm font-medium">Email</Label>
                                <Input
                                  value={branchFormData.email || ''}
                                  onChange={(e) => handleBranchFieldChange('email', e.target.value)}
                                  className="mt-1"
                                  placeholder="Optional"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        // View mode
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {getBranchTypeIcon(branch.branch_type)}
                              <h4 className="font-medium text-gray-900">{branch.name}</h4>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => handleEditBranch(branch)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 text-red-600 hover:text-red-800"
                                onClick={() => {
                                  setDeletingBranchId(branch.id)
                                  setShowBranchDeleteConfirm(true)
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          {branch.address && (
                            <p className="text-sm text-gray-600 mb-1">{branch.address}</p>
                          )}
                          <div className="flex items-center gap-4 text-sm text-gray-600">
                            {branch.phone && (
                              <a href={`tel:${branch.phone}`} className="flex items-center gap-1 hover:text-blue-600">
                                <Phone className="w-3 h-3" />
                                {branch.phone}
                              </a>
                            )}
                            {branch.email && (
                              <a href={`mailto:${branch.email}`} className="flex items-center gap-1 hover:text-blue-600">
                                <Mail className="w-3 h-3" />
                                {branch.email}
                              </a>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Error Display */}
          {validationErrors.submit && (
            <Alert className="border-red-200 bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-800">
                {validationErrors.submit}
              </AlertDescription>
            </Alert>
          )}

          {/* Branch Delete Confirmation Dialog */}
          <Dialog open={showBranchDeleteConfirm} onOpenChange={setShowBranchDeleteConfirm}>
            <DialogContent className="odin-modal-small">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-500" />
                  Delete Branch
                </DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete this branch? This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setShowBranchDeleteConfirm(false)
                  setDeletingBranchId(null)
                }}>
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteBranch(deletingBranchId)}
                >
                  Delete Branch
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </DialogContent>
    </Dialog>
  )
})

CustomerEditModal.displayName = 'CustomerEditModal'

/**
 * Format session timestamp with hybrid approach:
 * - Recent sessions (< 24h): "Started 2 hours ago"
 * - Older sessions: "Started 19 Oct, 14:30"
 *
 * @param {string} timestamp - ISO timestamp from session.created_at
 * @returns {string} Formatted time display
 */
const formatSessionTime = (timestamp) => {
  if (!timestamp) return ''

  const date = new Date(timestamp)
  const hoursSinceCreation = differenceInHours(new Date(), date)

  if (hoursSinceCreation < 24) {
    // Recent sessions: show relative time
    return `Started ${formatDistanceToNow(date, { addSuffix: true })}`
  } else {
    // Older sessions: show absolute time in British format
    return `Started ${format(date, 'd MMM, HH:mm')}`
  }
}

export default function ImportWizardV2({ user, onLogout }) {
  const navigate = useNavigate()

  // Data caching for customers, products, and product types
  const { getCustomers, getProducts, getProductTypes, invalidateCache } = useDataCache()

  const [currentStep, setCurrentStep] = useState('upload')
  const [csvData, setCsvData] = useState([])
  const [error, setError] = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const [isUploadingFile, setIsUploadingFile] = useState(false)
  const [csvHeaders, setCsvHeaders] = useState([])
  const [columnMappings, setColumnMappings] = useState({})
  const [selectedRecordIndex, setSelectedRecordIndex] = useState(0)
  const [recordStatuses, setRecordStatuses] = useState({})
  const [filteredCsvData, setFilteredCsvData] = useState([])
  const [recordEdits, setRecordEdits] = useState({})

  // New state for entity management
  const [customers, setCustomers] = useState([])

  // Legacy entity mappings - can be removed when Find & Match v2 is complete
  // const [entityMappings, setEntityMappings] = useState({
  //   customers: {}, // CSV value -> {customerId, customerName, branchId, branchName}
  //   products: {}   // CSV value -> {productId, productName}
  // })
  const [showCustomerModal, setShowCustomerModal] = useState(false)
  const [showBranchModal, setShowBranchModal] = useState(false)
  const [showProductTypeModal, setShowProductTypeModal] = useState(false)
  // Customer CRUD modal state
  const [showCustomerEditModal, setShowCustomerEditModal] = useState(false)
  const [editingCustomer, setEditingCustomer] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteError, setDeleteError] = useState(null)
  const [modalFormData, setModalFormData] = useState({})

  // State for Find & Match All confirmation dialog
  const [showMatchConfirmDialog, setShowMatchConfirmDialog] = useState(false)
  const [pendingMatchData, setPendingMatchData] = useState({ index: null, matchType: null, matchCount: 0, conflictedRecords: [] })
  // State for showing completed records toggle (defaults to false = auto-hide completed)
  const [showCompletedRecords, setShowCompletedRecords] = useState(true)

  // Track records that were just matched but haven't had Match Similar used yet
  const [recentlyMatchedRecords, setRecentlyMatchedRecords] = useState(new Set())

  // State for branch filtering based on selected customer
  const [filteredBranches, setFilteredBranches] = useState({})

  // State for validation errors
  const [validationErrors, setValidationErrors] = useState({})

  // State for shake animation
  const [shakeButton, setShakeButton] = useState(false)

  // State for smart customer/branch mappings
  const [smartMappings, setSmartMappings] = useState({}) // CSV value -> {customerId, customerName, branchId, branchName}
  const [manualOverrides, setManualOverrides] = useState(new Set()) // Set of record indices that were manually edited

  // State for product configuration step
  const [uniqueProducts, setUniqueProducts] = useState([])
  const [productConfigurations, setProductConfigurations] = useState({})
  const [existingProducts, setExistingProducts] = useState([])
  const [productMatches, setProductMatches] = useState({}) // Maps product variant key to existing product ID
  const [showImportSummaryModal, setShowImportSummaryModal] = useState(false)
  const [showProductConfigModal, setShowProductConfigModal] = useState(false)
  const [customerComboboxOpen, setCustomerComboboxOpen] = useState(false)
  const [productComboboxOpen, setProductComboboxOpen] = useState({}) // Track open state per product variant
  const [expandedVariantGroups, setExpandedVariantGroups] = useState({}) // Track which variant groups are expanded

  // State for new product type and product matching modals
  const [showProductTypeMatchModal, setShowProductTypeMatchModal] = useState(false)
  const [showProductStrainMatchModal, setShowProductStrainMatchModal] = useState(false)
  const [productTypeMappings, setProductTypeMappings] = useState({}) // CSV type -> { action, id/name, price_modifier_percent }
  const [productStrainMappings, setProductStrainMappings] = useState({}) // CSV strain -> { action, strain }
  const [existingProductTypes, setExistingProductTypes] = useState([])
  const [uniqueProductTypes, setUniqueProductTypes] = useState([])
  const [uniqueProductStrains, setUniqueProductStrains] = useState([])

  // State for resume loading modal
  const [isResuming, setIsResuming] = useState(false)
  const [isLoadingProductData, setIsLoadingProductData] = useState(false)

  // Computed value for displayed records - auto-hide completed unless toggle is on or recently matched
  const displayedRecords = useMemo(() => {
    console.log('🔍 [DISPLAY FILTER] Computing displayed records:', {
      showCompletedRecords,
      currentStep,
      totalRecords: filteredCsvData.length,
      recordStatuses: Object.keys(recordStatuses).length,
      recentlyMatchedRecords: Array.from(recentlyMatchedRecords)
    })

    if (showCompletedRecords || currentStep !== 'learn') {
      console.log('✅ [DISPLAY FILTER] Showing all records (toggle ON or not in learn step)')
      return filteredCsvData
    }

    // Auto-hide matched records by default, but keep recently matched records visible
    const filtered = filteredCsvData.filter((_, index) => {
      const status = recordStatuses[index]
      const isRecentlyMatched = recentlyMatchedRecords.has(index)

      if (status !== 'matched') {
        console.log(`📝 [DISPLAY FILTER] Record ${index}: status=${status || 'pending'} -> SHOW`)
        return true // Show all non-matched records
      }

      // Keep recently matched records visible (until Match Similar is used or user moves away)
      const shouldShow = isRecentlyMatched
      console.log(`🎯 [DISPLAY FILTER] Record ${index}: status=matched, recently=${isRecentlyMatched} -> ${shouldShow ? 'SHOW' : 'HIDE'}`)
      return shouldShow
    })

    console.log(`📊 [DISPLAY FILTER] Final result: ${filtered.length}/${filteredCsvData.length} records visible`)
    return filtered
  }, [filteredCsvData, showCompletedRecords, recordStatuses, currentStep, recentlyMatchedRecords])

  // Mapping from displayed index to original filteredCsvData index
  const displayedToOriginalIndex = useMemo(() => {
    if (showCompletedRecords || currentStep !== 'learn') {
      return filteredCsvData.map((_, index) => index)
    }

    const mapping = []
    filteredCsvData.forEach((_, index) => {
      const status = recordStatuses[index]
      if (status !== 'matched') {
        mapping.push(index)
      } else if (recentlyMatchedRecords.has(index)) {
        // Keep recently matched records in the mapping
        mapping.push(index)
      }
    })
    return mapping
  }, [filteredCsvData, showCompletedRecords, recordStatuses, currentStep, recentlyMatchedRecords])

  // Handle navigation when current record becomes hidden (auto-hide behavior)
  useEffect(() => {
    if (!showCompletedRecords && currentStep === 'learn' && displayedRecords.length > 0) {
      const currentDisplayedIndex = displayedToOriginalIndex.indexOf(selectedRecordIndex)
      if (currentDisplayedIndex === -1) {
        // Current record is hidden due to auto-hide, navigate to first available record
        setSelectedRecordIndex(displayedToOriginalIndex[0])
      }
    }
  }, [showCompletedRecords, selectedRecordIndex, displayedToOriginalIndex, displayedRecords.length, currentStep])

  // Handle auto-hide when user navigates away from recently matched record
  useEffect(() => {
    // When user changes selection, remove the previously selected record from recently matched
    // This allows the auto-hide to take effect when user moves to a different record
    const cleanupRecentlyMatched = () => {
      setRecentlyMatchedRecords(prev => {
        const newSet = new Set(prev)
        const removedRecords = []
        // Remove any records that are not currently selected
        for (const index of prev) {
          if (index !== selectedRecordIndex) {
            newSet.delete(index)
            removedRecords.push(index)
          }
        }
        if (removedRecords.length > 0) {
          console.log(`🧹 [CLEANUP] Removed records from recently matched:`, removedRecords, 'Current selection:', selectedRecordIndex)
          console.log(`🧹 [CLEANUP] Recently matched now:`, Array.from(newSet))
        }
        return newSet
      })
    }

    if (currentStep === 'learn') {
      console.log(`👆 [NAVIGATION] Selection changed to record ${selectedRecordIndex}, cleaning up recently matched`)
      cleanupRecentlyMatched()
    }
  }, [selectedRecordIndex, currentStep])

  const [showImportProgressModal, setShowImportProgressModal] = useState(false)
  const [importProgress, setImportProgress] = useState({ current: 0, total: 0, completed: false })
  const [showPreviewModal, setShowPreviewModal] = useState(false)

  // Import session management state
  const [importSessionId, setImportSessionId] = useState(null)
  const [sessionData, setSessionData] = useState(null) // eslint-disable-line no-unused-vars
  const [showRecoveryDialog, setShowRecoveryDialog] = useState(false)
  const [pendingSessions, setPendingSessions] = useState([])

  // Session check status (non-blocking)
  const [sessionCheckStatus, setSessionCheckStatus] = useState('idle') // 'idle' | 'checking' | 'success' | 'error'
  const [sessionCheckError, setSessionCheckError] = useState(null)
  const [showSessionCheckError, setShowSessionCheckError] = useState(false)

  // Template management state
  const [matchedTemplate, setMatchedTemplate] = useState(null)
  const [showTemplateDialog, setShowTemplateDialog] = useState(false)
  const [templateToApply, setTemplateToApply] = useState(null)
  const [showTemplateManager, setShowTemplateManager] = useState(false)
  const [userTemplates, setUserTemplates] = useState([])
  const [renamingTemplateId, setRenamingTemplateId] = useState(null)
  const [renameValue, setRenameValue] = useState('')

  // Import sessions management state
  const [showImportsManager, setShowImportsManager] = useState(false)
  const [importSessions, setImportSessions] = useState([])
  const [loadingImports, setLoadingImports] = useState(false)
  const [reImportDialogOpen, setReImportDialogOpen] = useState(false)
  const [selectedSessionDetail, setSelectedSessionDetail] = useState(null)
  const [showSessionDetail, setShowSessionDetail] = useState(false)
  const [selectedSessionForReimport, setSelectedSessionForReimport] = useState(null)
  const [reImportSummary, setReImportSummary] = useState(null)
  const [isReimporting, setIsReimporting] = useState(false)

  // Duplicate detection state (Phase 3)
  const [duplicateDetectionSummary, setDuplicateDetectionSummary] = useState(null)
  const [recordFingerprints, setRecordFingerprints] = useState(new Map()) // index -> {fingerprint, existing, action}
  const [showDeltaSummaryDialog, setShowDeltaSummaryDialog] = useState(false)

  const steps = [
    { id: 'upload', label: 'Upload', description: 'Select your CSV file' },
    { id: 'preview', label: 'Preview', description: 'Review your data' },
    { id: 'mapping', label: 'Mapping', description: 'Map CSV columns' },
    { id: 'learn', label: 'Learn', description: 'Match records' },
    { id: 'configure', label: 'Configure', description: 'Set up products' },
    { id: 'import', label: 'Import', description: 'Complete the import' }
  ]

  const currentStepIndex = steps.findIndex(step => step.id === currentStep)

  // Customer type metadata for grouping and display
  const customerTypeMetadata = useMemo(() => [
    { value: 'grocer', label: 'Grocers', icon: ShoppingCart },
    { value: 'restaurant', label: 'Restaurants', icon: UtensilsCrossed },
    { value: 'cafe', label: 'Cafes', icon: Coffee },
    { value: 'bakery', label: 'Bakeries', icon: CakeSlice },
    { value: 'direct', label: 'Direct', icon: Users },
    { value: 'market', label: 'Markets', icon: Store },
    { value: 'vegbox', label: 'VegBox', icon: Package },
    { value: 'distributor', label: 'Distributors', icon: Truck }
  ], [])

  // Sorted and grouped customers for dropdown
  const groupedCustomers = useMemo(() => {
    const groups = customerTypeMetadata.map(type => ({
      ...type,
      customers: customers
        .filter(c => c.type === type.value)
        .sort((a, b) => a.name.localeCompare(b.name))
    }))
    // Only return groups that have customers
    return groups.filter(g => g.customers.length > 0)
  }, [customers, customerTypeMetadata])

  // Grouped products by strain for dropdown
  const groupedProducts = useMemo(() => {
    // Get unique strains from existing products
    const strains = [...new Set(existingProducts.map(p => p.strain).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))

    // Create groups for each strain
    const groups = strains.map(strain => ({
      strain,
      products: existingProducts
        .filter(p => p.strain === strain)
        .sort((a, b) => (a.type || '').localeCompare(b.type || ''))
    }))

    // Add a group for products without strain
    const noStrainProducts = existingProducts
      .filter(p => !p.strain)
      .sort((a, b) => (a.type || '').localeCompare(b.type || ''))

    if (noStrainProducts.length > 0) {
      groups.push({
        strain: 'No Strain',
        products: noStrainProducts
      })
    }

    return groups.filter(g => g.products.length > 0)
  }, [existingProducts])

  // Customer lookup map for preview modal
  const customerLookup = useMemo(() => {
    const lookup = {}
    customers.forEach(customer => {
      lookup[customer.id] = customer
    })
    return lookup
  }, [customers])

  // Product configurations lookup for preview modal
  const productConfigs = useMemo(() => {
    return productConfigurations
  }, [productConfigurations])

  // Phase 2: Event-based auto-save - disabled time-based saving, use manual forceSave
  // Memoize state object to prevent unnecessary re-renders and infinite loops
  const autoSaveState = useMemo(() => ({
    csvData,
    csvHeaders,
    filteredCsvData,
    recordEdits,
    recordStatuses,
    smartMappings,
    manualOverrides,
    selectedRecordIndex,
    productMatches,
    productConfigurations,
    uniqueProducts,
    showCompletedRecords,
    filteredBranches,
    productTypeMappings,
    productStrainMappings
  }), [
    csvData,
    csvHeaders,
    filteredCsvData,
    recordEdits,
    recordStatuses,
    smartMappings,
    manualOverrides,
    selectedRecordIndex,
    productMatches,
    productConfigurations,
    uniqueProducts,
    showCompletedRecords,
    filteredBranches,
    productTypeMappings,
    productStrainMappings
  ])

  const { saveStatus, forceSave } = useAutoSave(importSessionId, autoSaveState, {
    enabled: false, // Disabled - manual saves only (90% write reduction vs original)
    debounceMs: 15000
  })

  // Load customers on component mount using cached data
  useEffect(() => {
    const loadEntities = async () => {
      try {
        const customersData = await getCustomers()
        setCustomers(customersData)
      } catch (err) {
        console.error('Failed to load customers:', err)
        setError('Failed to load existing customers')
      }
    }

    loadEntities()
  }, [getCustomers])

  // Check for existing incomplete sessions on component mount (non-blocking with retry)
  useEffect(() => {
    const checkForPendingSessions = async () => {
      if (!user?.id) return

      setSessionCheckStatus('checking')
      setSessionCheckError(null)
      setShowSessionCheckError(false)

      try {
        // Use retry logic with exponential backoff
        // Note: session_state is excluded by default for performance
        console.log('[Session Check] Fetching sessions for user:', user.id)
        const sessions = await retryDatabaseQuery(
          () => fetchUserImportSessions(user.id, { limit: 20, timeout: 15000, includeSessionState: false }),
          {
            maxAttempts: 3,
            initialDelay: 1000,
            maxDelay: 3000,
            onRetry: (error, attempt, delay) => {
              console.log(`🔄 Retry attempt ${attempt} after ${delay}ms due to:`, error.message)
            }
          }
        )

        console.log('[Session Check] Fetched sessions:', sessions.length, sessions.map(s => ({ file: s.file_name, status: s.status })))

        const incompleteSessions = sessions.filter(session =>
          ['uploading', 'parsing', 'mapping', 'validating', 'ready'].includes(session.status)
        )

        console.log('[Session Check] Incomplete sessions:', incompleteSessions.length)

        setSessionCheckStatus('success')

        if (incompleteSessions.length > 0) {
          setPendingSessions(incompleteSessions)
          // Non-blocking: show notification banner instead of modal
          // User can continue working and choose to resume later
        }
      } catch (error) {
        console.error('Failed to check for pending sessions:', error)
        setSessionCheckStatus('error')
        setSessionCheckError(error)

        // Non-blocking: don't prevent user from using the wizard
        // Show dismissible error notification
        // User can still start new import
      }
    }

    checkForPendingSessions()
  }, [user])

  // Save state before browser unload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if ((currentStep === 'learn' || currentStep === 'configure') && importSessionId) {
        forceSave() // Attempt final save
        // Note: We don't prevent unload, just trigger save
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [currentStep, importSessionId, forceSave])

  // Smart debounced auto-save during learn/configure steps when data changes
  // Saves 3 seconds after the last user action
  useEffect(() => {
    if (!importSessionId || (currentStep !== 'learn' && currentStep !== 'configure')) {
      return
    }

    // Debounce saves - wait 3 seconds after last change before saving
    const saveTimer = setTimeout(() => {
      console.log('[Auto-save] Triggering debounced save after user action (3s cooldown)')
      forceSave()
    }, 3000) // 3 seconds after last change

    return () => clearTimeout(saveTimer)
  }, [
    importSessionId,
    currentStep,
    recordStatuses,
    recordEdits,
    productMatches,
    productConfigurations,
    smartMappings,
    selectedRecordIndex,
    productTypeMappings,
    productStrainMappings,
    forceSave
  ])

  // Fuzzy matching utility: Calculate similarity between two strings using Jaccard coefficient
  const calculateSimilarity = useCallback((str1, str2) => {
    if (!str1 || !str2) return 0

    const set1 = new Set(str1.toLowerCase().split(''))
    const set2 = new Set(str2.toLowerCase().split(''))

    const intersection = new Set([...set1].filter(x => set2.has(x)))
    const union = new Set([...set1, ...set2])

    if (union.size === 0) return 0
    return intersection.size / union.size
  }, [])

  // Find the best customer match for a given CSV customer name
  const findBestCustomerMatch = useCallback((csvCustomerName, customersList, threshold = 0.9) => {
    if (!csvCustomerName || !customersList || customersList.length === 0) {
      return null
    }

    const csvNameLower = csvCustomerName.toLowerCase().trim()

    // First check for exact match (case-insensitive)
    const exactMatch = customersList.find(c =>
      c.name.toLowerCase().trim() === csvNameLower
    )
    if (exactMatch) {
      return { customer: exactMatch, matchType: 'exact', confidence: 1.0 }
    }

    // Then check fuzzy matches
    let bestMatch = null
    let bestSimilarity = 0

    customersList.forEach(customer => {
      const similarity = calculateSimilarity(csvCustomerName, customer.name)
      if (similarity >= threshold && similarity > bestSimilarity) {
        bestMatch = customer
        bestSimilarity = similarity
      }
    })

    if (bestMatch) {
      return { customer: bestMatch, matchType: 'fuzzy', confidence: bestSimilarity }
    }

    return null
  }, [calculateSimilarity])

  // Update filtered branches when record selection changes
  useEffect(() => {
    if (recordEdits[selectedRecordIndex]?.customerId && customers.length > 0) {
      const customer = customers.find(c => c.id === recordEdits[selectedRecordIndex].customerId)
      if (customer) {
        setFilteredBranches(prev => ({
          ...prev,
          [selectedRecordIndex]: customer.branches || []
        }))
      }
    }
  }, [selectedRecordIndex, recordEdits, customers])

  // Auto-suggest customer when row is selected (Phase 1 & 2: Smart Mappings + Fuzzy Matching)
  useEffect(() => {
    // Only run in learn step with valid data
    if (currentStep !== 'learn' || !filteredCsvData[selectedRecordIndex] || customers.length === 0) {
      return
    }

    const currentRecord = filteredCsvData[selectedRecordIndex]
    const csvCustomerName = currentRecord.customer

    // Skip if no customer name in CSV
    if (!csvCustomerName) {
      return
    }

    // Skip if already has a customer assigned (already matched or manually selected)
    if (recordEdits[selectedRecordIndex]?.customerId) {
      return
    }

    // Skip if this record has been manually overridden (user explicitly changed it)
    if (manualOverrides.has(selectedRecordIndex)) {
      return
    }

    // Phase 1: Check if we have an existing smart mapping for this CSV customer value
    if (smartMappings[csvCustomerName]) {
      const mapping = smartMappings[csvCustomerName]
      console.log(`🎯 [AUTO-SUGGEST] Applying smart mapping for "${csvCustomerName}" to record ${selectedRecordIndex}`)

      setRecordEdits(prev => ({
        ...prev,
        [selectedRecordIndex]: {
          ...prev[selectedRecordIndex],
          customerId: mapping.customerId,
          customerName: mapping.customerName,
          branchId: mapping.branchId,
          branchName: mapping.branchName
        }
      }))

      // Update filtered branches if customer has branches
      const customer = customers.find(c => c.id === mapping.customerId)
      if (customer?.branches) {
        setFilteredBranches(prev => ({
          ...prev,
          [selectedRecordIndex]: customer.branches
        }))
      }

      return // Smart mapping applied, don't do fuzzy matching
    }

    // Phase 2: Try fuzzy matching if no smart mapping exists
    const matchResult = findBestCustomerMatch(csvCustomerName, customers, 0.9)

    if (matchResult) {
      const { customer, matchType, confidence } = matchResult
      console.log(`✨ [AUTO-SUGGEST] ${matchType === 'exact' ? 'Exact' : 'Fuzzy'} match found for "${csvCustomerName}" → "${customer.name}" (confidence: ${confidence.toFixed(2)})`)

      setRecordEdits(prev => ({
        ...prev,
        [selectedRecordIndex]: {
          ...prev[selectedRecordIndex],
          customerId: customer.id,
          customerName: customer.name,
          // Auto-select branch if customer has only one branch or "Main / No Branch"
          branchId: customer.branches?.length === 1 ? customer.branches[0].id : null,
          branchName: customer.branches?.length === 1 ? customer.branches[0].branch_name : null
        }
      }))

      // Update filtered branches
      if (customer.branches) {
        setFilteredBranches(prev => ({
          ...prev,
          [selectedRecordIndex]: customer.branches
        }))
      }
    } else {
      console.log(`ℹ️  [AUTO-SUGGEST] No match found for "${csvCustomerName}"`)
    }
  }, [selectedRecordIndex, currentStep, filteredCsvData, customers, recordEdits, smartMappings, manualOverrides])

  // Odin field options for dropdowns
  const odinFields = [
    { value: 'none', label: 'None' },
    { value: 'date', label: 'Date' },
    { value: 'customer', label: 'Customer' },
    { value: 'branch', label: 'Branch' },
    { value: 'product', label: 'Product Type' },
    { value: 'strain', label: 'Strain' },
    { value: 'quantity', label: 'Quantity' },
    { value: 'price', label: 'Price' },
    { value: 'sales_id', label: 'Sales ID' },
    { value: 'invoice', label: 'Invoice Number' },
    { value: 'discount', label: 'Discount' },
    { value: 'packed', label: 'Packed Status' }
  ]

  // Required fields for validation
  const requiredFields = ['date', 'customer', 'product', 'quantity', 'price']

  // Auto-detection logic for matching CSV headers to Odin fields
  const getOdinFieldMatch = (csvHeader) => {
    const headerLower = csvHeader.toLowerCase().trim()

    // Priority 0: Exclude subtotal fields (calculated fields that should be omitted)
    const subtotalPatterns = ['subtotal', 'sub-total', 'sub total', 'total', 'line_total', 'amount_total']
    const isSubtotalField = subtotalPatterns.some(pattern => headerLower.includes(pattern))

    if (isSubtotalField) {
      return 'none'
    }

    // Priority 1: Check for price indicators (highest priority)
    const priceIndicators = ['price', '£', '$', 'cost', 'rate', 'unit_price', 'price_per_kg', 'price/kg', 'price per kg']
    const isPriceField = priceIndicators.some(indicator => headerLower.includes(indicator))

    if (isPriceField) {
      return 'price'
    }

    // Priority 2: Check for other specific field patterns
    const fieldMappings = {
      'date': ['date', 'sale_date', 'order_date', 'transaction_date'],
      'customer': ['customer', 'client', 'buyer', 'customer_name', 'customer name'],
      'branch': ['branch', 'location', 'site', 'branch_name'],
      'product': ['product', 'item', 'product_name', 'product name'],
      'strain': ['strain', 'variety', 'type', 'product_type'],
      'quantity': ['quantity', 'qty', 'units', 'weight', 'pounds', 'lbs'],
      'consignment': ['tray', 'tray_number', 'tray number', 'consignment', 'cons', 'cons_no'],
      'invoice': ['invoice', 'invoice_no', 'invoice_number', 'invoice #', 'invoice#'],
      'discount': ['discount', 'discount_percent', 'discount %'],
      'packed': ['packed', 'packing', 'pack_status', 'packing_status']
    }

    // Check for exact matches
    for (const [odinField, patterns] of Object.entries(fieldMappings)) {
      if (patterns.some(pattern => headerLower === pattern || headerLower.includes(pattern))) {
        return odinField
      }
    }

    // Priority 3: Fallback patterns for ambiguous cases
    const fallbackMappings = {
      'quantity': ['amount', 'kg'] // Only match these if not already caught by price logic
    }

    // Only check fallback patterns if no exact match found
    for (const [odinField, patterns] of Object.entries(fallbackMappings)) {
      if (patterns.some(pattern => headerLower.includes(pattern))) {
        // Skip if it looks like a price field
        if (isPriceField) {
          continue
        }
        return odinField
      }
    }

    return 'none'
  }

  // Auto-map columns when transitioning to mapping step
  const autoMapColumns = (headers) => {
    const mappings = {}
    headers.forEach(header => {
      mappings[header] = getOdinFieldMatch(header)
    })
    return mappings
  }

  // Update column mapping
  const updateMapping = (csvHeader, odinField) => {
    setColumnMappings(prev => ({
      ...prev,
      [csvHeader]: odinField
    }))
  }

  // Apply template mappings to current import
  const applyTemplate = useCallback((template) => {
    console.log('[applyTemplate] Applying template:', template.name)

    // Apply column mappings
    if (template.column_mappings && Object.keys(template.column_mappings).length > 0) {
      console.log('[applyTemplate] Applying column mappings')
      setColumnMappings(template.column_mappings)
    }

    // Apply smart mappings (customer/branch auto-matches)
    if (template.smart_mappings && Object.keys(template.smart_mappings).length > 0) {
      console.log('[applyTemplate] Applying smart mappings')
      setSmartMappings(template.smart_mappings)
    }

    // Apply product mappings
    if (template.product_mappings && Object.keys(template.product_mappings).length > 0) {
      console.log('[applyTemplate] Applying product mappings')
      setProductMatches(template.product_mappings)
    }

    // Update template last_used timestamp
    updateTemplateLastUsed(template.id).catch(err => {
      console.error('[applyTemplate] Failed to update template last_used:', err)
    })

    console.log('[applyTemplate] Template applied successfully')
  }, [])

  // Load user templates
  const loadUserTemplates = useCallback(async () => {
    try {
      const templates = await fetchUserTemplates(user.id)
      setUserTemplates(templates)
    } catch (error) {
      console.error('[loadUserTemplates] Failed to fetch templates:', error)
      setError('Failed to load templates')
    }
  }, [user.id])

  // Handle template deletion
  const handleDeleteTemplate = useCallback(async (templateId) => {
    try {
      await deleteTemplate(templateId, user.id)
      // Reload templates
      await loadUserTemplates()
    } catch (error) {
      console.error('[handleDeleteTemplate] Failed to delete template:', error)
      setError('Failed to delete template')
    }
  }, [user.id, loadUserTemplates])

  // Handle template rename
  const handleRenameTemplate = useCallback(async (templateId, newName) => {
    try {
      await renameTemplate(templateId, newName, user.id)
      // Reload templates
      await loadUserTemplates()
      setRenamingTemplateId(null)
      setRenameValue('')
    } catch (error) {
      console.error('[handleRenameTemplate] Failed to rename template:', error)
      setError('Failed to rename template')
    }
  }, [user.id, loadUserTemplates])

  // Open template manager and load templates
  const openTemplateManager = useCallback(async () => {
    await loadUserTemplates()
    setShowTemplateManager(true)
  }, [loadUserTemplates])

  // Load import sessions for re-import
  const loadImportSessions = useCallback(async () => {
    if (!user?.id) return

    setLoadingImports(true)
    try {
      const sessions = await fetchImportSessionsForReimport(user.id)
      setImportSessions(sessions)
    } catch (error) {
      console.error('[loadImportSessions] Failed to fetch import sessions:', error)
      setError('Failed to load import sessions')
    } finally {
      setLoadingImports(false)
    }
  }, [user?.id])

  // Open imports manager
  const openImportsManager = useCallback(async () => {
    await loadImportSessions()
    setShowImportsManager(true)
  }, [loadImportSessions])

  // Handle viewing session details
  const handleViewSessionDetail = useCallback((session) => {
    setSelectedSessionDetail(session)
    setShowSessionDetail(true)
  }, [])

  // Handle re-import initiation
  const handleInitiateReimport = useCallback(async (session) => {
    setSelectedSessionForReimport(session)
    setReImportDialogOpen(true)
  }, [])

  // Execute re-import
  const handleExecuteReimport = useCallback(async (skipDuplicates = true) => {
    if (!selectedSessionForReimport || !user?.id) return

    try {
      setIsReimporting(true)
      const result = await reImportFromSession(
        selectedSessionForReimport.id,
        user.id,
        skipDuplicates
      )

      setReImportSummary(result)

      // If successful, navigate to the new session or show summary
      if (result.newSession) {
        setReImportDialogOpen(false)
        setShowImportsManager(false)

        // Show success message
        const message = skipDuplicates
          ? `Re-import started: ${result.recordsToImport} records (${result.skippedDuplicates} duplicates skipped)`
          : `Re-import started: ${result.totalRecords} records`

        setError(null)
        setSuccessMessage(message)

        // Navigate to review step with the new session
        // (Implementation depends on your workflow - you may want to load the new session)
      }
    } catch (error) {
      console.error('[handleExecuteReimport] Error:', error)
      setError(`Failed to re-import: ${error.message}`)
    } finally {
      setIsReimporting(false)
    }
  }, [selectedSessionForReimport, user?.id])

  // Run duplicate detection on filtered CSV data
  const runDuplicateDetection = useCallback(async () => {
    if (!filteredCsvData || filteredCsvData.length === 0) {
      console.log('[runDuplicateDetection] No data to check')
      return
    }

    try {
      console.log('[runDuplicateDetection] Checking for duplicates...')

      const summary = await getDuplicateDetectionSummary(
        filteredCsvData,
        user.id,
        matchedTemplate?.id || null
      )

      console.log('[runDuplicateDetection] Summary:', summary)
      setDuplicateDetectionSummary(summary)
      setRecordFingerprints(summary.fingerprintMap)

      // Show delta summary if duplicates found
      if (summary.duplicateCount > 0) {
        setShowDeltaSummaryDialog(true)
      }
    } catch (error) {
      console.error('[runDuplicateDetection] Failed:', error)
      // Non-critical - don't fail the import
    }
  }, [filteredCsvData, user.id, matchedTemplate])

  // Check if required fields are mapped
  const hasRequiredMappings = () => {
    const mappedFields = Object.values(columnMappings)
    return requiredFields.every(field => mappedFields.includes(field))
  }


  // Check if a field is mapped
  const isMapped = (field) => {
    return Object.values(columnMappings).includes(field)
  }

  // Transform data for learning step - filter to only mapped columns
  const transformDataForLearning = () => {
    const mappedColumns = Object.entries(columnMappings)
      .filter(([, odinField]) => odinField !== 'none')

    const transformed = csvData.map((row, index) => {
      const newRow = { _originalIndex: index }
      mappedColumns.forEach(([csvHeader, odinField]) => {
        let value = row[csvHeader]

        // Parse price values - remove currency symbols and parse to number
        if (odinField === 'price' && (value || value === 0)) {
          // Remove £, $, €, commas, and spaces, then parse
          const cleanedValue = value.toString().replace(/[£$€,\s]/g, '')
          const numericValue = parseFloat(cleanedValue)
          value = isNaN(numericValue) ? null : numericValue
        }

        newRow[odinField] = value
      })
      return newRow
    })

    return transformed
  }

  // Get mapped column headers (Odin field names) in preferred order
  const getMappedHeaders = () => {
    // Define preferred column order: Sales ID, Date, Customer, Branch, Strain, Product, Quantity/Price/Discount, Invoice, Packed
    const preferredOrder = [
      'sales_id',
      'date',
      'customer',
      'branch',
      'strain',
      'product',
      'quantity',
      'price',
      'discount',
      'invoice',
      'packed'
    ]

    const mappedFields = Object.entries(columnMappings)
      .filter(([, odinField]) => odinField !== 'none')
      .map(([, odinField]) => odinField)

    // Sort by preferred order, keeping unmapped fields at the end
    return mappedFields.sort((a, b) => {
      const indexA = preferredOrder.indexOf(a)
      const indexB = preferredOrder.indexOf(b)

      // If both are in preferred order, sort by index
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB
      }
      // If only A is in preferred order, A comes first
      if (indexA !== -1) return -1
      // If only B is in preferred order, B comes first
      if (indexB !== -1) return 1
      // If neither is in preferred order, maintain current order
      return 0
    })
  }

  // Update record status
  const updateRecordStatus = (index, status) => {
    setRecordStatuses(prev => ({
      ...prev,
      [index]: status
    }))
  }

  // Validate restored entities still exist in database
  const validateRestoredEntities = useCallback((state, customersData, productsData) => {
    const warnings = []
    const updatedRecordEdits = { ...state.recordEdits }
    const updatedProductMatches = { ...state.productMatches }

    // Check customer IDs in record edits
    Object.keys(updatedRecordEdits).forEach((indexStr) => {
      const index = parseInt(indexStr)
      const edit = updatedRecordEdits[index]

      if (edit.customerId) {
        const customerExists = customersData.find(c => c.id === edit.customerId)
        if (!customerExists) {
          warnings.push(`Record ${index + 1}: Customer was deleted, needs re-matching`)
          // Clear the invalid customer and branch
          delete updatedRecordEdits[index].customerId
          delete updatedRecordEdits[index].customerName
          delete updatedRecordEdits[index].branchId
          delete updatedRecordEdits[index].branchName
        }
      }
    })

    // Check product IDs in product matches
    Object.keys(updatedProductMatches).forEach((key) => {
      const productId = updatedProductMatches[key]
      const productExists = productsData.find(p => p.id === productId)
      if (!productExists) {
        warnings.push(`Product variant "${key.split('|||').join(' - ')}" was deleted, needs re-matching`)
        delete updatedProductMatches[key]
      }
    })

    if (warnings.length > 0) {
      console.warn('Session recovery warnings:', warnings)
      setError(`Recovered session with ${warnings.length} ${warnings.length === 1 ? 'warning' : 'warnings'}. Some entities were deleted and need re-matching.`)
    }

    return { updatedRecordEdits, updatedProductMatches, warnings }
  }, [])

  // Session recovery functions
  const handleResumeSession = async (sessionId) => {
    try {
      console.log('🔄 [Resume] Starting session resume for:', sessionId)
      setIsResuming(true)
      setShowRecoveryDialog(false)
      setError(null)

      // Fetch session with state
      console.log('🔄 [Resume] Fetching session data...')
      const session = await fetchImportSession(sessionId)
      console.log('🔄 [Resume] Session fetched:', { id: session.id, status: session.status, hasState: !!session.session_state })

      // Log session state size for debugging
      console.log('🔄 [Resume] Session state value:', session.session_state)
      if (session.session_state) {
        const stateSize = JSON.stringify(session.session_state).length
        console.log('🔄 [Resume] Session state size:', (stateSize / 1024).toFixed(2), 'KB')
        console.log('🔄 [Resume] Session state keys:', Object.keys(session.session_state))
      } else {
        console.log('🔄 [Resume] Session state is null/undefined')
      }

      console.log('🔄 [Resume] About to set state...')
      setImportSessionId(sessionId)
      console.log('🔄 [Resume] ImportSessionId set')
      setSessionData(session)
      console.log('🔄 [Resume] SessionData set')

      // Check if session has saved state
      console.log('🔄 [Resume] Validating session state...')
      if (!session.session_state || !isValidSessionState(session.session_state)) {
        console.warn('🔄 [Resume] Session state validation failed:', {
          hasState: !!session.session_state,
          stateType: typeof session.session_state,
          isValid: session.session_state ? isValidSessionState(session.session_state) : false
        })

        // Show better error message
        const hasEmptyState = session.session_state && Object.keys(session.session_state).length === 0
        const errorMsg = !session.session_state || hasEmptyState
          ? 'This session has no saved progress. You may have closed the import before completing any steps. Please start a new import.'
          : 'This session data is invalid or missing required fields. Please start a new import.'

        setError(errorMsg)
        setCurrentStep('upload')
        setIsResuming(false)

        // Mark session as abandoned in database so it won't show up again
        try {
          await updateImportSession(sessionId, { status: 'abandoned' })
        } catch (updateError) {
          console.error('Failed to mark session as abandoned:', updateError)
        }

        // Remove this session from pending list since it can't be resumed
        setPendingSessions(prev => prev.filter(s => s.id !== sessionId))

        // Auto-dismiss the error after 10 seconds
        setTimeout(() => setError(null), 10000)
        return
      }

      // Fetch fresh entity data (may have changed since save) - force refresh to get latest
      console.log('🔄 [Resume] Fetching customers and products...')
      const [customersData, productsData] = await Promise.all([
        getCustomers(true), // Force refresh on session restore
        getProducts(true)
      ])
      console.log('🔄 [Resume] Entities fetched:', { customers: customersData.length, products: productsData.length })
      setCustomers(customersData)
      setExistingProducts(productsData)

      // Restore session state
      const restoredState = loadSessionState(session.session_state)

      // Validate and fix entity references
      const { updatedRecordEdits, updatedProductMatches } = validateRestoredEntities(
        restoredState,
        customersData,
        productsData
      )

      // Restore CSV data
      setCsvData(restoredState.csvData || [])
      setCsvHeaders(restoredState.csvHeaders || [])
      setFilteredCsvData(restoredState.filteredCsvData || [])

      // Restore record state (with validated edits)
      setRecordEdits(updatedRecordEdits)
      setRecordStatuses(restoredState.recordStatuses || {})
      setSmartMappings(restoredState.smartMappings || {})
      setManualOverrides(restoredState.manualOverrides || new Set())
      setSelectedRecordIndex(restoredState.selectedRecordIndex || 0)

      // Restore product state (with validated matches)
      setProductMatches(updatedProductMatches)
      setProductConfigurations(restoredState.productConfigurations || {})
      setUniqueProducts(restoredState.uniqueProducts || [])
      setShowCompletedRecords(restoredState.showCompletedRecords || false)
      setFilteredBranches(restoredState.filteredBranches || {})

      // Restore product type and strain mappings
      console.log('🔄 [Session Restore] Product type mappings:', restoredState.productTypeMappings)
      console.log('🔄 [Session Restore] Product strain mappings:', restoredState.productStrainMappings)
      setProductTypeMappings(restoredState.productTypeMappings || {})
      setProductStrainMappings(restoredState.productStrainMappings || {})

      // Restore column mappings if available
      if (session.column_mappings && Object.keys(session.column_mappings).length > 0) {
        setColumnMappings(session.column_mappings)
      }

      // Determine correct step based on status
      let targetStep = 'upload'
      if (session.status === 'ready') {
        targetStep = 'configure'
      } else if (session.status === 'validating') {
        targetStep = 'learn'
      } else if (session.status === 'mapping') {
        targetStep = 'mapping'
      }

      setCurrentStep(targetStep)

      console.log('Session recovered successfully:', {
        sessionId,
        status: session.status,
        targetStep,
        recordsCount: restoredState.csvData?.length || 0,
        matchedCount: Object.values(restoredState.recordStatuses || {}).filter(s => s === 'matched').length
      })

      setIsResuming(false)

    } catch (error) {
      console.error('❌ [Resume] Failed to resume session:', error)
      console.error('❌ [Resume] Error details:', {
        message: error.message,
        stack: error.stack,
        name: error.name
      })

      // Provide user-friendly error message
      let errorMessage = 'Failed to resume import session. '
      if (error.message.includes('fetch') || error.message.includes('network')) {
        errorMessage += 'Please check your internet connection and try again.'
      } else if (error.message.includes('timeout')) {
        errorMessage += 'The request timed out. Please try again.'
      } else {
        errorMessage += 'Please try starting a new import.'
      }

      setError(errorMessage)
      setShowRecoveryDialog(false)
      setIsResuming(false)
      setCurrentStep('upload') // Ensure we're back at upload step

      // Mark session as abandoned in database so it won't show up again
      try {
        await updateImportSession(sessionId, { status: 'abandoned' })
      } catch (updateError) {
        console.error('Failed to mark session as abandoned:', updateError)
      }

      // Remove this session from pending list since it failed to resume
      setPendingSessions(prev => prev.filter(s => s.id !== sessionId))

      // Auto-dismiss the error after 10 seconds
      setTimeout(() => setError(null), 10000)
    }
  }

  const handleAbandonSessions = async () => {
    try {
      // Mark all pending sessions as abandoned
      for (const session of pendingSessions) {
        await updateImportSession(session.id, { status: 'abandoned' })
      }

      setShowRecoveryDialog(false)
      setPendingSessions([])

    } catch (error) {
      console.error('Failed to abandon sessions:', error)
      setError('Failed to clean up pending sessions.')
    }
  }

  const handleStartNewSession = () => {
    setShowRecoveryDialog(false)
    // Continue with normal flow - existing sessions remain pending
  }

  // Customer CRUD handlers

  const handleUpdateCustomer = async (customerId, formData) => {
    try {
      await updateCustomer(customerId, formData)

      // Update customers state
      setCustomers(prev => prev.map(customer =>
        customer.id === customerId
          ? { ...customer, ...formData }
          : customer
      ))

      setShowCustomerEditModal(false)
      setEditingCustomer(null)
      setModalFormData({})
    } catch (error) {
      console.error('Failed to update customer:', error)
      setError('Failed to update customer. Please try again.')
    }
  }

  const checkCustomerHasMatches = (customerId) => {
    return Object.values(recordEdits).some(edit => edit.customerId === customerId)
  }

  // Function to unmatch all records for a specific customer
  const unmatchCustomerRecords = (customerId) => {
    const indicesToUnmatch = []

    // Find all record indices that have this customer
    Object.entries(recordEdits).forEach(([index, edit]) => {
      if (edit.customerId === customerId) {
        indicesToUnmatch.push(parseInt(index))
      }
    })

    // Unmatch all found records
    setRecordStatuses(prev => {
      const newStatuses = { ...prev }
      indicesToUnmatch.forEach(index => {
        delete newStatuses[index]  // Remove from matched status (returns to pending)
      })
      return newStatuses
    })

    // Clear customer selections from recordEdits for these records
    setRecordEdits(prev => {
      const newEdits = { ...prev }
      indicesToUnmatch.forEach(index => {
        if (newEdits[index]) {
          // Remove customer-related fields but keep other edits
          const { customerId: _customerId, branchId: _branchId, ...otherEdits } = newEdits[index]
          if (Object.keys(otherEdits).length > 0) {
            newEdits[index] = otherEdits
          } else {
            delete newEdits[index]
          }
        }
      })
      return newEdits
    })

    return indicesToUnmatch.length // Return number of records unmatched
  }

  // Function to handle delete and unmatch
  const handleDeleteAndUnmatch = async (customerId) => {
    try {
      // First, unmatch all records for this customer
      const unmatchedCount = unmatchCustomerRecords(customerId)

      // Then delete the customer
      await deleteCustomer(customerId)

      // Remove customer from state
      setCustomers(prev => prev.filter(customer => customer.id !== customerId))

      // Close modals and reset state
      setShowDeleteConfirm(false)
      setShowCustomerEditModal(false)
      setEditingCustomer(null)
      setModalFormData({})
      setDeleteError(null)

      console.log(`Deleted customer and unmatched ${unmatchedCount} records`)
    } catch (error) {
      console.error('Failed to delete customer and unmatch records:', error)
      setDeleteError('Failed to delete customer and unmatch records. Please try again.')
    }
  }

  const handleDeleteCustomer = async (customerId) => {
    try {
      // Check if customer has matched records
      const hasMatches = checkCustomerHasMatches(customerId)

      if (hasMatches) {
        setDeleteError('Cannot delete customer with matched records. Please unmatch records first.')
        setShowDeleteConfirm(true)
        return
      }

      await deleteCustomer(customerId)

      // Remove customer from state
      setCustomers(prev => prev.filter(customer => customer.id !== customerId))

      setShowCustomerEditModal(false)
      setEditingCustomer(null)
      setModalFormData({})
    } catch (error) {
      console.error('Failed to delete customer:', error)
      setDeleteError('Failed to delete customer. Please try again.')
      setShowDeleteConfirm(true)
    }
  }

  // Handle field changes in record detail panel
  const handleFieldChange = (field, value) => {
    setRecordEdits(prev => ({
      ...prev,
      [selectedRecordIndex]: {
        ...prev[selectedRecordIndex],
        [field]: value
      }
    }))
  }

  // Get current record data (with edits applied)
  const getCurrentRecordData = (index) => {
    const originalData = filteredCsvData[index] || {}
    const edits = recordEdits[index] || {}

    // filteredCsvData is already transformed with Odin field names,
    // so just merge with edits without re-transforming
    return { ...originalData, ...edits }
  }

  // Helper function to apply smart mappings to all matching records
  const applySmartMapping = (csvValue, customerId, customerName, branchId, branchName) => {
    if (!csvValue) return

    // Store the mapping
    setSmartMappings(prev => ({
      ...prev,
      [csvValue]: {
        customerId,
        customerName,
        branchId,
        branchName
      }
    }))

    // Apply to all records with same CSV value (except manually overridden ones)
    const recordsToUpdate = []
    filteredCsvData.forEach((record, index) => {
      if (record.customer === csvValue && !manualOverrides.has(index)) {
        recordsToUpdate.push(index)
      }
    })

    if (recordsToUpdate.length > 1) {
      setRecordEdits(prev => {
        const newEdits = { ...prev }
        recordsToUpdate.forEach(index => {
          newEdits[index] = {
            ...newEdits[index],
            customerId,
            customerName,
            branchId,
            branchName
          }
        })
        return newEdits
      })

      // Show notification about the batch update
      console.log(`✨ Smart Mapping: Applied customer/branch to ${recordsToUpdate.length} records with value "${csvValue}"`)

      // You could add a toast notification here in the future
      // For now, the visual feedback in the table and notification box is sufficient
    }
  }

  // Helper function to count records that would be affected by smart mapping
  const countMatchingRecords = (csvValue) => {
    if (!csvValue) return 0
    return filteredCsvData.filter((record, index) =>
      record.customer === csvValue && !manualOverrides.has(index)
    ).length
  }

  // Helper function to check for conflicts with manual overrides in Match Similar
  const checkMatchSimilarConflicts = (index, matchType = 'all') => {
    const currentRecord = filteredCsvData[index]
    const originalCsvCustomer = currentRecord.customer
    const currentEdits = recordEdits[index] || {}

    const conflictedRecords = []
    const safeRecords = []

    if (matchType === 'all') {
      filteredCsvData.forEach((record, recordIndex) => {
        if (recordIndex === index) return // Skip current record

        const customerMatches = record.customer === originalCsvCustomer
        const hasRequiredMappings = currentEdits.customerId &&
            Object.prototype.hasOwnProperty.call(currentEdits, 'branchId') &&
            (currentEdits.product || record.product)

        if (customerMatches && hasRequiredMappings) {
          if (manualOverrides.has(recordIndex)) {
            conflictedRecords.push(recordIndex)
          } else {
            safeRecords.push(recordIndex)
          }
        }
      })
    }

    return { conflictedRecords, safeRecords, totalMatches: conflictedRecords.length + safeRecords.length }
  }

  // Helper function to trigger auto-matching when navigating away from a record
  const handleNavigationAutoMatch = (fromIndex) => {
    const edits = recordEdits[fromIndex]
    if (edits?.customerId && !manualOverrides.has(fromIndex)) {
      const csvValue = filteredCsvData[fromIndex]?.customer
      if (csvValue) {
        console.log(`🚀 Navigation auto-match triggered for "${csvValue}" from record ${fromIndex}`)
        applySmartMapping(csvValue, edits.customerId, edits.customerName, edits.branchId, edits.branchName)
      }
    }
  }

  // Navigation functions
  const navigateRecord = (direction) => {
    // Trigger auto-match before navigation
    handleNavigationAutoMatch(selectedRecordIndex)

    const maxDisplayedIndex = displayedRecords.length - 1
    const currentDisplayedIndex = displayedToOriginalIndex.indexOf(selectedRecordIndex)

    if (direction === 'next' && currentDisplayedIndex < maxDisplayedIndex) {
      const nextOriginalIndex = displayedToOriginalIndex[currentDisplayedIndex + 1]
      setSelectedRecordIndex(nextOriginalIndex)
    } else if (direction === 'prev' && currentDisplayedIndex > 0) {
      const prevOriginalIndex = displayedToOriginalIndex[currentDisplayedIndex - 1]
      setSelectedRecordIndex(prevOriginalIndex)
    } else if (direction === 'first') {
      const firstOriginalIndex = displayedToOriginalIndex[0]
      setSelectedRecordIndex(firstOriginalIndex)
    } else if (direction === 'last') {
      const lastOriginalIndex = displayedToOriginalIndex[maxDisplayedIndex]
      setSelectedRecordIndex(lastOriginalIndex)
    }
  }

  // Action handlers
  const handleMatch = (index) => {
    const currentStatus = recordStatuses[index]
    if (currentStatus === 'matched') {
      // Unmatch - return to pending state
      setRecordStatuses(prev => {
        const newStatuses = { ...prev }
        delete newStatuses[index]
        return newStatuses
      })
      // Clear any validation errors when unmatching
      setValidationErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[index]
        return newErrors
      })
      // Remove from recently matched records set
      setRecentlyMatchedRecords(prev => {
        const newSet = new Set(prev)
        newSet.delete(index)
        return newSet
      })
    } else {
      // Validate required fields before matching
      const edits = recordEdits[index] || {}
      const hasCustomer = !!edits.customerId
      const hasBranch = Object.prototype.hasOwnProperty.call(edits, 'branchId')

      if (!hasCustomer || !hasBranch) {
        // Set validation errors for missing fields
        setValidationErrors(prev => ({
          ...prev,
          [index]: {
            customer: !hasCustomer,
            branch: !hasBranch
          }
        }))

        // Trigger shake animation
        setShakeButton(true)
        setTimeout(() => setShakeButton(false), 500)

        return // Prevent match
      }

      // Clear any existing validation errors and proceed with match
      setValidationErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[index]
        return newErrors
      })
      updateRecordStatus(index, 'matched')

      // Add to recently matched records set to keep it visible for Match Similar
      setRecentlyMatchedRecords(prev => {
        const newSet = new Set([...prev, index])
        console.log(`✨ [RECENTLY MATCHED] Added record ${index} to recently matched set:`, Array.from(newSet))
        return newSet
      })
    }
  }

  const handleFindAndMatch = (index, matchType = 'all') => {
    const currentRecord = filteredCsvData[index]
    const originalCsvCustomer = currentRecord.customer
    const originalCsvBranch = currentRecord.branch
    const originalCsvProduct = currentRecord.product
    const originalCsvStrain = currentRecord.strain
    const currentEdits = recordEdits[index] || {}

    if (matchType === 'all') {
      // For 'all' mode: match ALL records with same CSV customer value
      filteredCsvData.forEach((record, recordIndex) => {
        if (recordIndex === index) return // Skip current record

        // Check if CSV customer matches
        const customerMatches = record.customer === originalCsvCustomer

        // Only proceed if customer matches AND we have required mappings
        if (customerMatches && currentEdits.customerId &&
            Object.prototype.hasOwnProperty.call(currentEdits, 'branchId') &&
            (currentEdits.product || record.product)) {

          console.log(`Match Similar: Matching record ${recordIndex} with customer "${originalCsvCustomer}"`)

          // Apply customer, branch, and product mappings
          setRecordEdits(prev => ({
            ...prev,
            [recordIndex]: {
              ...prev[recordIndex],
              customerId: currentEdits.customerId,
              customerName: currentEdits.customerName,
              branchId: currentEdits.branchId,
              branchName: currentEdits.branchName,
              product: currentEdits.product || record.product,
              strain: currentEdits.strain || record.strain
            }
          }))

          // Update filtered branches for this record
          if (currentEdits.customerId) {
            const customer = customers.find(c => c.id === currentEdits.customerId)
            if (customer) {
              setFilteredBranches(prev => ({
                ...prev,
                [recordIndex]: customer.branches || []
              }))
            }
          }

          updateRecordStatus(recordIndex, 'matched')
        }
      })
    } else if (matchType === 'customer') {
      // For 'customer' mode: match only by customer (existing behavior for customer-only button)
      filteredCsvData.forEach((record, recordIndex) => {
        if (recordIndex === index) return // Skip current record

        if (record.customer === originalCsvCustomer && currentEdits.customerId) {
          const updateData = {
            customerId: currentEdits.customerId,
            customerName: currentEdits.customerName
          }

          // Include branch if available
          if (Object.prototype.hasOwnProperty.call(currentEdits, 'branchId')) {
            updateData.branchId = currentEdits.branchId
            updateData.branchName = currentEdits.branchName
          }

          setRecordEdits(prev => ({
            ...prev,
            [recordIndex]: {
              ...prev[recordIndex],
              ...updateData
            }
          }))

          // Update filtered branches for this record too
          if (currentEdits.customerId) {
            const customer = customers.find(c => c.id === currentEdits.customerId)
            if (customer) {
              setFilteredBranches(prev => ({
                ...prev,
                [recordIndex]: customer.branches || []
              }))
            }
          }

          updateRecordStatus(recordIndex, 'matched')
        }
      })
    }

    // Mark current record as matched too
    updateRecordStatus(index, 'matched')
  }

  const handleDisregard = (index) => {
    const currentStatus = recordStatuses[index]
    if (currentStatus === 'disregarded') {
      // Undo disregard - return to pending state
      setRecordStatuses(prev => {
        const newStatuses = { ...prev }
        delete newStatuses[index]
        return newStatuses
      })
    } else {
      updateRecordStatus(index, 'disregarded')
    }
  }

  // Get row styling based on status
  const getRowClassName = (index, status) => {
    let className = 'cursor-pointer transition-colors '

    if (index === selectedRecordIndex) {
      className += 'bg-blue-50 border-l-4 border-blue-500 '
    } else if (status === 'disregarded') {
      className += 'opacity-50 bg-gray-50 '
    } else if (status === 'matched') {
      className += 'bg-green-50 '
    } else {
      className += 'hover:bg-gray-50 '
    }

    return className
  }

  // Check if record is ready for Find & Match All (requires customer, branch selection, product, and record to be matched)
  const isReadyForFindAndMatchAll = (index) => {
    const edits = recordEdits[index] || {}
    const recordData = getCurrentRecordData(index)
    const hasCustomer = !!edits.customerId

    // Check if customer is Direct type - Direct customers don't need branch selection
    const customer = customers.find(c => c.id === edits.customerId)
    const isDirectCustomer = customer?.type === 'direct'

    const hasBranchSelection = isDirectCustomer || Object.prototype.hasOwnProperty.call(edits, 'branchId') // null is valid (Main/No Branch)
    const hasProduct = !!(edits.product || recordData.product) // Check both edits and original CSV data
    const isMatched = recordStatuses[index] === 'matched'

    return hasCustomer && hasBranchSelection && hasProduct && isMatched
  }

  // Count potential matches for Find & Match All
  const countPotentialMatches = (index, matchType = 'all') => {
    const currentRecord = filteredCsvData[index]
    const originalCsvCustomer = currentRecord.customer
    const originalCsvBranch = currentRecord.branch
    const originalCsvProduct = currentRecord.product
    const originalCsvStrain = currentRecord.strain
    const currentEdits = recordEdits[index] || {}


    let matchCount = 0

    if (matchType === 'all') {
      filteredCsvData.forEach((record, recordIndex) => {
        if (recordIndex === index) return // Skip current record

        // Check if CSV customer matches (new simplified logic)
        const customerMatches = record.customer === originalCsvCustomer

        const hasRequiredMappings = currentEdits.customerId &&
            Object.prototype.hasOwnProperty.call(currentEdits, 'branchId') &&
            (currentEdits.product || record.product)

        // Count if customer matches AND we have required mappings
        if (customerMatches && hasRequiredMappings) {
          matchCount++
        }
      })
    } else if (matchType === 'customer') {
      filteredCsvData.forEach((record, recordIndex) => {
        if (recordIndex === index) return // Skip current record

        if (record.customer === originalCsvCustomer && currentEdits.customerId) {
          matchCount++
        }
      })
    }

    return matchCount
  }

  // Handle Find & Match All with confirmation dialog
  const handleFindAndMatchWithConfirmation = (index, matchType = 'all') => {
    const conflicts = checkMatchSimilarConflicts(index, matchType)

    if (conflicts.totalMatches === 0) {
      // No matches found, just proceed with marking current record
      handleFindAndMatch(index, matchType)
      // Remove from recently matched since Match Similar was attempted
      setRecentlyMatchedRecords(prev => {
        const newSet = new Set(prev)
        newSet.delete(index)
        return newSet
      })
      return
    }

    // Show confirmation dialog with conflict information
    setPendingMatchData({
      index,
      matchType,
      matchCount: conflicts.totalMatches,
      conflictedRecords: conflicts.conflictedRecords,
      safeRecords: conflicts.safeRecords
    })
    setShowMatchConfirmDialog(true)
  }

  // Confirm the Find & Match All action - force overwrite all
  const confirmFindAndMatchOverwriteAll = () => {
    console.log('Confirming Find & Match with OVERWRITE ALL:', pendingMatchData)
    // Temporarily remove manual overrides for conflicted records
    const originalOverrides = new Set(manualOverrides)
    const updatedOverrides = new Set(originalOverrides)
    pendingMatchData.conflictedRecords.forEach(recordIndex => {
      updatedOverrides.delete(recordIndex)
    })
    setManualOverrides(updatedOverrides)

    handleFindAndMatch(pendingMatchData.index, pendingMatchData.matchType)

    // Remove from recently matched since Match Similar was used successfully
    setRecentlyMatchedRecords(prev => {
      const newSet = new Set(prev)
      newSet.delete(pendingMatchData.index)
      return newSet
    })
    setShowMatchConfirmDialog(false)
    setPendingMatchData({ index: null, matchType: null, matchCount: 0, conflictedRecords: [] })
  }

  // Match only the current record (overwrite this record only)
  const confirmFindAndMatchThisRecordOnly = () => {
    console.log('Confirming Find & Match THIS RECORD ONLY:', pendingMatchData)
    // Just mark the current record as matched, don't affect others
    updateRecordStatus(pendingMatchData.index, 'matched')

    // Remove from recently matched
    setRecentlyMatchedRecords(prev => {
      const newSet = new Set(prev)
      newSet.delete(pendingMatchData.index)
      return newSet
    })
    setShowMatchConfirmDialog(false)
    setPendingMatchData({ index: null, matchType: null, matchCount: 0, conflictedRecords: [] })
  }

  // Cancel the operation
  const cancelFindAndMatch = () => {
    console.log('Cancelling Find & Match operation')
    setShowMatchConfirmDialog(false)
    setPendingMatchData({ index: null, matchType: null, matchCount: 0, conflictedRecords: [] })
  }

  // Legacy function for backwards compatibility (no conflicts)
  const confirmFindAndMatch = () => {
    console.log('Confirming Find & Match with data:', pendingMatchData)
    handleFindAndMatch(pendingMatchData.index, pendingMatchData.matchType)
    // Remove from recently matched since Match Similar was used successfully
    setRecentlyMatchedRecords(prev => {
      const newSet = new Set(prev)
      newSet.delete(pendingMatchData.index)
      return newSet
    })
    setShowMatchConfirmDialog(false)
    setPendingMatchData({ index: null, matchType: null, matchCount: 0, conflictedRecords: [] })
  }

  // Optimized modal form field update helper - memoized to prevent recreation
  const updateModalField = useCallback((fieldName) => (value) => {
    setModalFormData(prev => ({
      ...prev,
      [fieldName]: value
    }))
  }, [])

  // Handle proceeding to next step
  const handleContinue = async () => {
    if (currentStep === 'preview') {
      // Auto-map columns and transition to mapping step
      const mappings = autoMapColumns(csvHeaders)
      setColumnMappings(mappings)
      setCurrentStep('mapping')

      // Update session status and save column mappings
      if (importSessionId) {
        try {
          await updateImportSession(importSessionId, {
            status: 'mapping',
            column_mappings: mappings
          })
        } catch (error) {
          console.error('Failed to update session status:', error)
        }
      }
    } else if (currentStep === 'mapping') {
      // Transform data for learning step
      const transformedData = transformDataForLearning()
      setFilteredCsvData(transformedData)
      setSelectedRecordIndex(0)
      setRecordStatuses({})
      setCurrentStep('learn')

      // Update session status to learning and save state
      if (importSessionId) {
        try {
          await updateImportSession(importSessionId, { status: 'validating' })
          // Force save session state after transition
          await forceSave()
        } catch (error) {
          console.error('Failed to update session status:', error)
        }
      }

      // Run duplicate detection on transformed data
      // Wait for state update, then run detection
      setTimeout(async () => {
        try {
          const summary = await getDuplicateDetectionSummary(
            transformedData,
            user.id,
            matchedTemplate?.id || null
          )
          console.log('[Duplicate Detection] Summary:', summary)
          setDuplicateDetectionSummary(summary)
          setRecordFingerprints(summary.fingerprintMap)

          if (summary.duplicateCount > 0) {
            setShowDeltaSummaryDialog(true)
          }
        } catch (error) {
          console.error('[Duplicate Detection] Failed:', error)
        }
      }, 100)
    }
  }

  // Handle proceeding to product configuration
  const handleProceedToProductConfiguration = async () => {
    try {
      // Fetch existing products from cache
      const productsData = await getProducts()
      setExistingProducts(productsData)

      // Extract unique strain+product combinations from matched records
      const productVariantsMap = new Map() // Map to track unique combinations with record count

      filteredCsvData.forEach((record, index) => {
        // Only include matched records (not disregarded)
        if (recordStatuses[index] === 'matched') {
          const recordData = getCurrentRecordData(index)
          const strain = recordData.strain?.trim() || ''
          const productType = recordData.product?.trim() || ''

          // Only add if we have at least one of strain or product type
          if (strain || productType) {
            // Create a composite key for this variant
            const variantKey = `${strain}|||${productType}` // Use ||| as delimiter to avoid conflicts

            if (productVariantsMap.has(variantKey)) {
              productVariantsMap.set(variantKey, {
                ...productVariantsMap.get(variantKey),
                recordCount: productVariantsMap.get(variantKey).recordCount + 1
              })
            } else {
              productVariantsMap.set(variantKey, {
                strain,
                productType,
                recordCount: 1
              })
            }
          }
        }
      })

      // Convert map to array with composite keys
      const uniqueProductsList = Array.from(productVariantsMap.entries()).map(([key, data]) => ({
        key, // Composite key for lookups
        strain: data.strain,
        productType: data.productType,
        displayName: `${data.strain}${data.strain && data.productType ? ' - ' : ''}${data.productType}`,
        recordCount: data.recordCount,
        selected: true // Default all to selected
      }))

      // Group similar product variants (e.g., "A class", "A Class", "AClass" → single group)
      const groupedProducts = groupProductVariants(uniqueProductsList)

      // Normalize groups to have the same structure as original products for backward compatibility
      const normalizedGroups = groupedProducts.map(group => normalizeGroupForDisplay(group))

      setUniqueProducts(normalizedGroups)

      // Auto-match products to existing products
      // Match based on strain + name (where name = productType in CSV)
      const autoMatches = {}
      uniqueProductsList.forEach(product => {
        const csvStrain = product.strain.toLowerCase().trim()
        const csvProductType = product.productType.toLowerCase().trim()

        // Try to find exact match on strain + type
        const existingProduct = productsData.find(p => {
          const dbStrain = (p.strain || '').toLowerCase().trim()
          const dbType = (p.type || '').toLowerCase().trim()

          return dbStrain === csvStrain && dbType === csvProductType
        })

        if (existingProduct) {
          autoMatches[product.key] = existingProduct.id
        }
      })
      setProductMatches(autoMatches)

      // Initialize configurations for each product variant
      const initialConfigs = {}
      uniqueProductsList.forEach(product => {
        const matchedProduct = productsData.find(p => p.id === autoMatches[product.key])

        if (matchedProduct) {
          // Pre-fill with existing product data
          initialConfigs[product.key] = {
            strain: matchedProduct.strain || '',
            type: matchedProduct.type || '',
            sku: matchedProduct.sku || '',
            basePrice: matchedProduct.base_price_per_kg || '',
            active: matchedProduct.active !== false
          }
        } else {
          // Pre-fill with CSV data for new products
          initialConfigs[product.key] = {
            strain: product.strain,
            type: '', // Type is a different field (fresh/dried), not productType
            sku: '',
            basePrice: '',
            active: true
          }
        }
      })
      setProductConfigurations(initialConfigs)

      // Update session status to ready for configuration
      if (importSessionId) {
        try {
          await updateImportSession(importSessionId, {
            status: 'ready',
            processed_rows: filteredCsvData.filter((_, index) => recordStatuses[index] === 'matched').length
          })
          // Force save session state before product configuration
          await forceSave()
        } catch (error) {
          console.error('Failed to update session status:', error)
        }
      }

      setShowProductConfigModal(true)
    } catch (error) {
      console.error('Failed to load products:', error)
      setError('Failed to load existing products')
    }
  }

  // Handle showing import summary modal
  const handleShowImportSummary = () => {
    setShowImportSummaryModal(true)
  }

  // Handle disregarding all unmatched records
  const handleDisregardAllUnmatched = () => {
    const updatedStatuses = { ...recordStatuses }

    filteredCsvData.forEach((_, index) => {
      // Only update records that are currently unmatched (not matched or disregarded)
      if (!updatedStatuses[index]) {
        updatedStatuses[index] = 'disregarded'
      }
    })

    setRecordStatuses(updatedStatuses)
  }

  // Handle proceeding from summary to product type matching
  const handleProceedFromSummary = async () => {
    setShowImportSummaryModal(false)
    setIsLoadingProductData(true)

    try {
      console.log('🚀 [handleProceedFromSummary] Current productTypeMappings:', productTypeMappings)

      // Fetch existing product types and products from cache
      const [productTypesData, productsData] = await Promise.all([
        getProductTypes(),
        getProducts()
      ])

      console.log('📦 [handleProceedFromSummary] Fetched product types:', productTypesData.map(t => ({ id: t.id, name: t.name })))

      setExistingProductTypes(productTypesData)
      setExistingProducts(productsData)

      // Extract unique product types from matched records
      const typesSet = new Set()
      filteredCsvData.forEach((record, index) => {
        if (recordStatuses[index] === 'matched') {
          const recordData = getCurrentRecordData(index)
          const productType = recordData.product?.trim()
          if (productType) {
            typesSet.add(productType)
          }
        }
      })

      const uniqueTypesArray = Array.from(typesSet)
      console.log('📝 [handleProceedFromSummary] Unique types from CSV:', uniqueTypesArray)
      setUniqueProductTypes(uniqueTypesArray)

      setIsLoadingProductData(false)

      // Show product type matching modal
      setShowProductTypeMatchModal(true)
    } catch (error) {
      console.error('Failed to load product data:', error)
      setIsLoadingProductData(false)
      alert('Failed to load product data: ' + error.message)
    }
  }

  // Handle confirming product type mappings
  const handleConfirmProductTypes = (typeMappings) => {
    console.log('Product type mappings confirmed:', typeMappings)
    setProductTypeMappings(typeMappings)
    setShowProductTypeMatchModal(false)

    // Extract unique strains only
    const strainsSet = new Set()
    filteredCsvData.forEach((record, index) => {
      if (recordStatuses[index] === 'matched') {
        const recordData = getCurrentRecordData(index)
        const strain = recordData.strain?.trim() || ''

        if (strain) {
          strainsSet.add(strain)
        }
      }
    })

    const uniqueStrainsArray = Array.from(strainsSet)
    setUniqueProductStrains(uniqueStrainsArray)

    // Show product strain matching modal
    setShowProductStrainMatchModal(true)
  }

  // Handle confirming strain mappings
  const handleConfirmStrains = (strainMappings) => {
    console.log('Product strain mappings confirmed:', strainMappings)
    setProductStrainMappings(strainMappings)
    setShowProductStrainMatchModal(false)

    // Show preview modal
    setShowPreviewModal(true)
  }

  // Handle confirming product configuration and show preview (old flow - kept for backward compatibility)
  const handleConfirmProductConfiguration = () => {
    setShowProductConfigModal(false)
    setShowPreviewModal(true)
  }

  // Handle confirming preview and proceed to import
  const handleConfirmPreview = () => {
    setShowPreviewModal(false)
    setShowImportProgressModal(true)

    // Start the import process
    handleStartImport()
  }

  // Handle the actual import process using staging workflow
  const handleStartImport = async () => {
    try {
      // Update session status to importing
      if (importSessionId) {
        await updateImportSession(importSessionId, { status: 'importing' })
      }

      // Get records to import (only matched, non-disregarded records)
      const recordsToImport = filteredCsvData
        .map((record, index) => ({ record, index }))
        .filter(({ index }) => recordStatuses[index] === 'matched')

      const totalRecords = recordsToImport.length
      setImportProgress({ current: 0, total: totalRecords + 2, completed: false }) // +2 for products and final commit

      // Step 1A: Create new product types first
      console.log('Creating new product types...')
      const typeIdMap = {} // Maps CSV type name to created/matched ID
      const disregardedTypes = new Set() // Track types to exclude from import

      for (const [csvType, mapping] of Object.entries(productTypeMappings)) {
        if (mapping.action === 'disregard') {
          // Mark type to be disregarded - records with this type will be excluded
          console.log(`Disregarding type: ${csvType}`)
          disregardedTypes.add(csvType)
        } else if (mapping.action === 'create') {
          // This should no longer happen - types are now created immediately in the modal
          console.log(`Creating new product type: ${mapping.name}`)
          const newType = await createProductType({
            name: mapping.name,
            price_modifier_percent: mapping.price_modifier_percent || 0,
            active: true
          })
          typeIdMap[csvType] = newType.id
          // Invalidate product types cache after creation
          invalidateCache('productTypes')
        } else if (mapping.action === 'match-new') {
          // Matched to a newly created type - use the ID from the source type
          console.log(`Mapping ${csvType} to newly created type: ${mapping.name}`)
          typeIdMap[csvType] = typeIdMap[mapping.sourceType]
        } else {
          // Matched to existing database type (including types just created in the modal)
          typeIdMap[csvType] = mapping.id
        }
      }

      // Step 1B: Build products from strain + type mappings
      console.log('Building products from strain and type mappings...')
      const productIdMap = {} // Maps "resolvedStrain|resolvedType" key to product ID

      // Get all unique strain+type combinations from the CSV data
      const productCombos = new Set()
      recordsToImport.forEach(({ index }) => {
        const recordData = getCurrentRecordData(index)
        const csvStrain = recordData.strain?.trim() || ''
        const csvType = recordData.product?.trim() || ''
        if (csvStrain && csvType && !disregardedTypes.has(csvType)) {
          productCombos.add(`${csvStrain}|${csvType}`)
        }
      })

      // Build price lookup map to assign prices to newly created products
      // Maps "resolvedStrain|resolvedType" to { price, date }
      const productPriceLookup = {}
      recordsToImport.forEach(({ index }) => {
        const recordData = getCurrentRecordData(index)
        const csvStrain = recordData.strain?.trim() || ''
        const csvType = recordData.product?.trim() || ''

        if (!csvStrain || !csvType || disregardedTypes.has(csvType)) return

        const strainMapping = productStrainMappings[csvStrain]
        const typeMapping = productTypeMappings[csvType]
        if (!strainMapping || !typeMapping) return

        const resolvedStrain = strainMapping.strain
        const resolvedType = typeMapping.name
        const productKey = `${resolvedStrain}|${resolvedType}`

        const price = parseFloat(recordData.price || 0)
        const date = recordData.date

        // Store price if this is the first record OR has a more recent date
        if (!productPriceLookup[productKey]) {
          productPriceLookup[productKey] = { price, date }
        } else {
          const currentDate = new Date(date)
          const storedDate = new Date(productPriceLookup[productKey].date)
          if (currentDate > storedDate) {
            productPriceLookup[productKey] = { price, date }
          }
        }
      })
      console.log('Product price lookup map built:', productPriceLookup)

      // For each combination, resolve the strain and type, then find or create the product
      for (const combo of productCombos) {
        const [csvStrain, csvType] = combo.split('|')

        // Resolve the strain (from strain mappings)
        const strainMapping = productStrainMappings[csvStrain]
        if (!strainMapping) continue
        const resolvedStrain = strainMapping.strain

        // Resolve the type name (from type mappings)
        const typeMapping = productTypeMappings[csvType]
        if (!typeMapping) continue
        const resolvedType = typeMapping.name

        const productKey = `${resolvedStrain}|${resolvedType}`

        // Check if this product already exists in the database
        const existingProduct = existingProducts.find(p =>
          (p.strain || '').toLowerCase().trim() === resolvedStrain.toLowerCase().trim() &&
          (p.type || '').toLowerCase().trim() === resolvedType.toLowerCase().trim()
        )

        if (existingProduct) {
          console.log(`Using existing product: ${resolvedStrain} - ${resolvedType}`)
          productIdMap[productKey] = existingProduct.id
        } else {
          // Create new product
          console.log(`Creating new product: ${resolvedStrain} - ${resolvedType}`)
          try {
            // Look up product_type_id from typeIdMap (maps CSV type to product_types.id)
            const productTypeId = typeIdMap[csvType] || null

            if (!productTypeId) {
              console.warn(`[ImportWizard] Product type ID not found for CSV type: "${csvType}". Product will be created without product_type_id.`)
            }

            const newProduct = await createProduct({
              strain: resolvedStrain,
              type: resolvedType,  // Keep for backward compatibility during transition
              product_type_id: productTypeId,  // NEW: FK to product_types table
              sku: null,
              active: true,
              base_price_per_kg: productPriceLookup[productKey]?.price ?? null
            })
            productIdMap[productKey] = newProduct.id
            // Invalidate products cache after creation
            invalidateCache('products')
          } catch (error) {
            // If unique constraint violation, product already exists - fetch it
            if (error.code === '23505') {
              console.log(`Product already exists (from concurrent import or previous failure): ${resolvedStrain} - ${resolvedType}`)
              const { data: fetchedProduct } = await supabase
                .from('products')
                .select('*')
                .eq('strain', resolvedStrain)
                .eq('type', resolvedType)
                .single()

              if (fetchedProduct) {
                productIdMap[productKey] = fetchedProduct.id
              } else {
                throw error // Re-throw if we couldn't fetch it
              }
            } else {
              throw error // Re-throw if it's not a duplicate key error
            }
          }
        }
      }

      setImportProgress({ current: 1, total: totalRecords + 2, completed: false })

      // Step 1C: Build product map for staging records (maps CSV strain|type to product ID)
      const productMap = {}

      recordsToImport.forEach(({ index }) => {
        const recordData = getCurrentRecordData(index)
        const csvStrain = recordData.strain?.trim() || ''
        const csvType = recordData.product?.trim() || ''

        // Skip products with disregarded types
        if (disregardedTypes.has(csvType)) {
          return
        }

        // Resolve strain and type
        const strainMapping = productStrainMappings[csvStrain]
        const typeMapping = productTypeMappings[csvType]

        if (strainMapping && typeMapping) {
          const resolvedStrain = strainMapping.strain
          const resolvedType = typeMapping.name
          const productKey = `${resolvedStrain}|${resolvedType}`

          if (productIdMap[productKey]) {
            // Create composite key for CSV data (used for lookups)
            const compositeKey = `${csvStrain}|||${csvType}`
            productMap[compositeKey] = {
              id: productIdMap[productKey],
              strain: resolvedStrain,
              type: resolvedType
            }
          }
        }
      })

      console.log('Product map built:', productMap)

      // Step 2: Create staging records
      setImportProgress({ current: 2, total: totalRecords + 2, completed: false })
      const stagingRecords = []

      for (let i = 0; i < recordsToImport.length; i++) {
        const { record, index } = recordsToImport[i]
        const recordData = getCurrentRecordData(index)

        // Build composite key from record data
        const strain = recordData.strain?.trim() || ''
        const productType = recordData.product?.trim() || ''
        const variantKey = `${strain}|||${productType}`

        // Skip if product not selected for import
        if (!productMap[variantKey]) {
          continue
        }

        const productId = productMap[variantKey].id
        const isNewProduct = productMatches[variantKey] === undefined

        // Get product_type_id from typeIdMap
        const productTypeId = typeIdMap[productType] || null

        const stagingRecord = {
          session_id: importSessionId,
          row_number: index + 1, // 1-based row numbers
          original_data: record, // Original CSV data
          mapped_data: {
            date: recordData.date,
            customer_id: recordData.customerId,
            branch_id: recordData.branchId === null ? null : recordData.branchId,
            sales_id: recordData.sales_id ? parseInt(recordData.sales_id) : null,
            product_id: productId,
            product_type_id: productTypeId, // Required for sale_items
            quantity: parseFloat(recordData.quantity || 0),
            price: parseFloat(recordData.price || 0) // Historical price from CSV is preserved
          },
          validation_status: 'valid',
          validation_errors: [],
          created_entities: isNewProduct ? {
            product_id: productId,
            product_type_id: productTypeId
          } : {}, // Only track created entities for new products
          imported: false
        }

        stagingRecords.push(stagingRecord)

        // Update progress every 10 records to show smooth progress
        if (i % 10 === 0 || i === recordsToImport.length - 1) {
          setImportProgress({ current: 2 + i, total: totalRecords + 2, completed: false })
          // Small delay to allow UI updates
          await new Promise(resolve => setTimeout(resolve, 10))
        }
      }

      // Batch insert staging records
      if (stagingRecords.length > 0) {
        await createStagingSalesRecords(stagingRecords)
      }

      // Step 3: Commit staging records to actual sales
      setImportProgress({ current: totalRecords + 1, total: totalRecords + 2, completed: false })
      const importResult = await commitStagingImportToSales(importSessionId)

      if (importResult.success) {
        // Mark as completed
        setImportProgress(prev => ({ ...prev, current: totalRecords + 2, completed: true }))

        // Create/update template for future imports
        try {
          console.log('[Import] Creating template for future imports...')

          // Generate template name from file name (remove extension)
          const templateName = uploadedFileName?.replace(/\.[^/.]+$/, '') || 'Import Template'

          await createTemplate({
            userId: user.id,
            name: templateName,
            headers: csvHeaders,
            columnMappings: columnMappings,
            smartMappings: smartMappings,
            productMappings: productMatches
          })

          console.log('[Import] Template created successfully')
        } catch (templateError) {
          console.error('[Import] Failed to create template:', templateError)
          // Non-critical - don't fail the import
        }

        // Save import history for duplicate detection (Phase 3)
        try {
          console.log('[Import] Saving import history for duplicate detection...')

          const historyRecords = []

          // Process each record in the CSV
          filteredCsvData.forEach((record, index) => {
            const status = recordStatuses[index]
            const fingerprint = recordFingerprints.get(index)?.fingerprint

            if (!fingerprint) {
              console.warn(`[Import] No fingerprint for record ${index}, skipping history`)
              return
            }

            // Save both imported and disregarded records
            if (status === 'matched' || status === 'disregarded') {
              const recordData = getCurrentRecordData(index)
              const mappedData = {}

              // For matched records, save the mapped entities
              if (status === 'matched') {
                const edits = recordEdits[index] || {}
                mappedData.customerId = edits.customer?.id || null
                mappedData.branchId = edits.branch?.id || null

                // Get product ID from productMatches
                const variantKey = `${recordData.strain}|||${recordData.product}`
                mappedData.productId = productMatches[variantKey] || null
              }

              historyRecords.push({
                sessionId: importSessionId,
                templateId: matchedTemplate?.id || null,
                fingerprint: fingerprint,
                action: status === 'matched' ? 'imported' : 'disregarded',
                mappedData: mappedData
              })
            }
          })

          if (historyRecords.length > 0) {
            await saveImportHistory(historyRecords)
            console.log(`[Import] Saved ${historyRecords.length} records to import history`)
          }
        } catch (historyError) {
          console.error('[Import] Failed to save import history:', historyError)
          // Non-critical - don't fail the import
        }
      } else {
        throw new Error(`Import completed with ${importResult.errorCount} errors`)
      }

    } catch (error) {
      console.error('Import failed:', error)
      setImportProgress(prev => ({
        ...prev,
        completed: false,
        error: error.message
      }))

      // Mark session as failed
      if (importSessionId) {
        try {
          await updateImportSession(importSessionId, {
            status: 'failed',
            error_message: error.message
          })
        } catch (updateError) {
          console.error('Failed to update session status:', updateError)
        }
      }
    }
  }

  return (
    <PageLayout
      title="Import Data"
      user={user}
      onLogout={onLogout}
    >
      <div className="h-full overflow-y-auto">
        <div className="page-title-section flex justify-between items-center mb-4">
          <Button
            variant="outline"
            onClick={openImportsManager}
          >
            Manage Imports
          </Button>
        </div>

        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-semibold text-gray-900 mb-2">Import Data</h1>
              <p className="text-gray-600">Import your CSV data with intelligent learning and mapping</p>
            </div>

            {/* Continue to Next Step button - always show in learn step */}
            {currentStep === 'learn' && filteredCsvData.length > 0 && (
              <Button
                variant="continue"
                size="lg"
                onClick={handleShowImportSummary}
                className="px-8"
              >
                Continue to Next Step
              </Button>
            )}
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription className="flex justify-between items-center">
              {error}
              <Button variant="ghost" size="sm" onClick={() => setError(null)}>×</Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Import Stages Card */}
        <Card className="mb-8 bg-white">
          <CardHeader>
            <CardTitle>Import Progress</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 rounded-full border-2 flex items-center justify-center text-sm font-medium transition-colors ${
                        index === currentStepIndex
                          ? 'bg-gray-900 text-white border-gray-900'
                          : index < currentStepIndex
                          ? 'bg-white text-green-600 border-green-600'
                          : 'bg-background text-muted-foreground border-muted-foreground/20'
                      }`}
                    >
                      {index + 1}
                    </div>
                    <div className="text-center mt-2">
                      <div className={`text-sm font-medium ${
                        index === currentStepIndex ? 'text-gray-900' : index < currentStepIndex ? 'text-green-600' : 'text-muted-foreground'
                      }`}>
                        {step.label}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {step.description}
                      </div>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div
                      className={`w-16 h-0.5 mx-4 transition-colors ${
                        index < currentStepIndex ? 'bg-primary' : 'bg-muted-foreground/20'
                      }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Non-blocking Session Notifications */}
        {currentStep === 'upload' && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-6">
            <div className="lg:col-span-3 space-y-3">
              {/* Resume Error Alert (Shows when resume fails) */}
              {error && (
                <Alert variant="destructive">
                  <AlertDescription className="flex justify-between items-center">
                    {error}
                    <Button variant="ghost" size="sm" onClick={() => setError(null)}>×</Button>
                  </AlertDescription>
                </Alert>
              )}

              {/* Session Check Loading */}
              {sessionCheckStatus === 'checking' && (
                <Alert className="border-blue-200 bg-blue-50">
                  <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
                  <AlertTitle className="text-blue-900">Checking for pending imports...</AlertTitle>
                  <AlertDescription className="text-blue-700">
                    Looking for incomplete import sessions that you can resume.
                  </AlertDescription>
                </Alert>
              )}

              {/* Session Check Error (Dismissible) */}
              {sessionCheckStatus === 'error' && sessionCheckError && !showSessionCheckError && (
                <Alert className="border-yellow-200 bg-yellow-50">
                  <AlertTriangle className="h-4 w-4 text-yellow-600" />
                  <div className="flex items-start justify-between flex-1">
                    <div>
                      <AlertTitle className="text-yellow-900">Unable to check for pending imports</AlertTitle>
                      <AlertDescription className="text-yellow-700">
                        {sessionCheckError.code === 'TIMEOUT'
                          ? 'The request timed out. You can still start a new import.'
                          : 'There was an error checking for incomplete sessions. You can still start a new import.'}
                      </AlertDescription>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowSessionCheckError(true)}
                      className="text-yellow-700 hover:text-yellow-900"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </Alert>
              )}

              {/* Pending Sessions Found (Non-blocking) */}
              {pendingSessions.length > 0 && sessionCheckStatus === 'success' && (
                <Alert className="border-green-200 bg-green-50">
                  <AlertTriangle className="h-4 w-4 text-green-600" />
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <AlertTitle className="text-green-900">
                        {pendingSessions.length} Incomplete Import{pendingSessions.length > 1 ? 's' : ''} Found
                      </AlertTitle>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPendingSessions([])}
                        className="text-green-700 hover:text-green-900 -mt-1"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    <AlertDescription className="text-green-700 space-y-3 mt-2">
                      <p>You have pending import sessions that you can resume or start fresh.</p>
                      <div className="space-y-2 mt-3">
                        {pendingSessions.slice(0, 3).map((session) => (
                          <div key={session.id} className="flex items-center justify-between p-2 bg-white rounded border border-green-200">
                            <div className="text-sm">
                              <div className="font-medium text-gray-900">{session.file_name}</div>
                              <div className="text-gray-600 text-xs">
                                {session.total_rows} rows • Status: {session.status} • {formatSessionTime(session.created_at)}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setPendingSessions([session])
                                setShowRecoveryDialog(true)
                              }}
                              className="ml-3 flex-shrink-0"
                            >
                              Resume
                            </Button>
                          </div>
                        ))}
                        {pendingSessions.length > 3 && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowRecoveryDialog(true)}
                            className="w-full"
                          >
                            View All {pendingSessions.length} Sessions
                          </Button>
                        )}
                      </div>
                    </AlertDescription>
                  </div>
                </Alert>
              )}
            </div>
          </div>
        )}

        {/* Two-column layout for upload, preview, mapping, and learning steps */}
        {(currentStep === 'upload' || currentStep === 'preview' || currentStep === 'mapping' || currentStep === 'learn') && (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* File Upload Card - 75% width */}
            <div className="lg:col-span-3">
              <Card className="bg-white">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <CardTitle>
                        {currentStep === 'upload' && 'Upload CSV File'}
                        {currentStep === 'preview' && 'Preview Your Data'}
                        {currentStep === 'mapping' && 'Map CSV Columns to Odin Fields'}
                        {currentStep === 'learn' && 'Review and Approve Records'}
                      </CardTitle>
                      {/* Auto-save status indicator */}
                      {importSessionId && (currentStep === 'learn' || currentStep === 'configure') && (
                        <div className="text-xs flex items-center gap-1.5">
                          {saveStatus === 'saving' && (
                            <>
                              <div className="h-2 w-2 rounded-full bg-yellow-500 animate-pulse" />
                              <span className="text-gray-500">Saving...</span>
                            </>
                          )}
                          {saveStatus === 'saved' && (
                            <>
                              <Check className="h-3 w-3 text-green-600" />
                              <span className="text-green-600">Saved</span>
                            </>
                          )}
                          {saveStatus === 'error' && (
                            <>
                              <AlertTriangle className="h-3 w-3 text-red-600" />
                              <span className="text-red-600">Save failed</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    {currentStep === 'learn' && filteredCsvData.length > 0 && (
                      <div className="flex items-center gap-2">
                        <ToggleSwitch
                          checked={!showCompletedRecords}
                          onCheckedChange={(newValue) => {
                            console.log('🔄 [TOGGLE] Toggle changed:', newValue ? 'HIDE matched records' : 'SHOW matched records')
                            setShowCompletedRecords(!newValue)
                          }}
                        />
                        <span className="text-sm text-muted-foreground">
                          Hide matched records
                        </span>
                      </div>
                    )}
                  </div>
                  {currentStep === 'preview' && csvData.length > 0 && (
                    <p className="text-sm text-muted-foreground">
                      Found {csvData.length} records in your CSV file
                    </p>
                  )}
                  {currentStep === 'mapping' && (
                    <p className="text-sm text-muted-foreground">
                      Review and adjust how your CSV columns map to Odin's database
                    </p>
                  )}
                  {currentStep === 'learn' && filteredCsvData.length > 0 && (
                    <p className="text-sm text-muted-foreground">
                      {Object.values(recordStatuses).filter(status => status === 'matched').length}/{filteredCsvData.length} records matched
                    </p>
                  )}
                </CardHeader>
                <CardContent>
                  {currentStep === 'upload' ? (
                    <div
                      className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                        isDragOver
                          ? 'border-primary bg-primary/5'
                          : 'border-muted-foreground/20 hover:border-muted-foreground/40'
                      }`}
                    onDragOver={(e) => {
                      e.preventDefault()
                      setIsDragOver(true)
                    }}
                    onDragLeave={(e) => {
                      e.preventDefault()
                      setIsDragOver(false)
                    }}
                    onDrop={(e) => {
                      e.preventDefault()
                      setIsDragOver(false)
                      const files = Array.from(e.dataTransfer.files)
                      const csvFile = files.find(file => file.type === 'text/csv' || file.name.endsWith('.csv'))
                      if (csvFile) {
                        handleFileUpload(csvFile)
                      } else {
                        setError('Please upload a valid CSV file')
                      }
                    }}
                  >
                    <div className="space-y-4">
                      <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center">
                        <svg
                          className="w-8 h-8 text-muted-foreground"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="text-lg font-medium text-gray-900 mb-1">
                          Drop your CSV file here
                        </p>
                        <p className="text-muted-foreground mb-4">
                          or click to browse your files
                        </p>
                        <Button variant="outline">
                          Choose File
                        </Button>
                        <input
                          type="file"
                          accept=".csv,text/csv"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0]
                            if (file) {
                              handleFileUpload(file)
                            }
                          }}
                        />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        Supports CSV files up to 10MB
                      </p>
                    </div>
                  </div>
                  ) : currentStep === 'preview' ? (
                    /* CSV Preview Table */
                    <>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            {csvData[0] && Object.keys(csvData[0]).map(header => (
                              <TableHead key={header}>
                                {header}
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {csvData.slice(0, 10).map((row, index) => (
                            <TableRow key={index}>
                              {Object.values(row).map((cell, cellIndex) => (
                                <TableCell key={cellIndex}>
                                  {String(cell).substring(0, 100)}
                                  {String(cell).length > 100 && '...'}
                                </TableCell>
                              ))}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      {csvData.length > 10 && (
                        <p className="text-sm text-muted-foreground mt-4">
                          Showing first 10 rows of {csvData.length} total records
                        </p>
                      )}
                    </>
                  ) : currentStep === 'mapping' ? (
                    /* Column Mapping Interface */
                    <div className="space-y-4">
                      {csvHeaders.map((header, index) => (
                        <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                          {/* CSV Column Name */}
                          <div className="flex-1 text-sm font-medium text-gray-900">
                            {header}
                          </div>

                          {/* Arrow Icon */}
                          <div className="flex-shrink-0">
                            <svg
                              className="w-4 h-4 text-muted-foreground"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </div>

                          {/* Odin Field Dropdown */}
                          <div className="flex-1">
                            <Select
                              value={columnMappings[header] || 'none'}
                              onValueChange={(value) => updateMapping(header, value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {odinFields.map(field => (
                                  <SelectItem key={field.value} value={field.value}>
                                    {field.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Learning Step - Filtered CSV Table */
                    <div className="w-full overflow-auto">
                      <Table className="w-full table-auto">
                        <TableHeader>
                          <TableRow>
                            {getMappedHeaders().map((header, headerIndex) => (
                              <TableHead key={headerIndex} className="whitespace-nowrap">
                                {header.charAt(0).toUpperCase() + header.slice(1)}
                              </TableHead>
                            ))}
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {displayedRecords.map((row, displayedIndex) => {
                            const originalIndex = displayedToOriginalIndex[displayedIndex]
                            return (
                              <TableRow
                                key={originalIndex}
                                className={getRowClassName(originalIndex, recordStatuses[originalIndex])}
                                onClick={() => {
                                  // Trigger auto-match before navigation
                                  handleNavigationAutoMatch(selectedRecordIndex)
                                  setSelectedRecordIndex(originalIndex)
                                }}
                              >
                                {getMappedHeaders().map((header, cellIndex) => (
                                  <TableCell key={cellIndex} className="whitespace-nowrap">
                                    {String(row[header] || '').substring(0, 100)}
                                    {String(row[header] || '').length > 100 && '...'}
                                  </TableCell>
                                ))}
                              </TableRow>
                            )
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Helper Card - 25% width */}
            <div className="lg:col-span-1">
              <div className="sticky top-6">
                <Card className="bg-white">
                <CardContent className="pt-6 pb-1">
                  {currentStep === 'upload' ? (
                    <div className="text-center space-y-4">
                      <div className="flex justify-center">
                        <svg
                          className="w-8 h-8 text-muted-foreground"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16l-4-4m0 0l4-4m-4 4h18"
                          />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 mb-1">
                          Upload file to continue
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Select your CSV file to begin the import process
                        </p>
                      </div>
                    </div>
                  ) : currentStep === 'preview' ? (
                    /* Record Information Panel */
                    <div className="space-y-4">
                      <div className="text-center">
                        <div className="flex justify-center mb-2">
                          <svg
                            className="w-8 h-8 text-primary"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            />
                          </svg>
                        </div>
                        <p className="font-medium text-gray-900 mb-1">
                          Data Preview
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Review your imported data
                        </p>
                      </div>

                      <div className="border-t pt-4">
                        <div className="space-y-3">
                          <div className="text-sm">
                            <span className="font-medium text-gray-900">Total Records:</span>
                            <span className="ml-2 text-muted-foreground">{csvData.length}</span>
                          </div>

                          {csvData[0] && (
                            <div className="text-sm">
                              <span className="font-medium text-gray-900">Columns:</span>
                              <span className="ml-2 text-muted-foreground">{Object.keys(csvData[0]).length}</span>
                            </div>
                          )}

                          {csvData[0] && (
                            <div className="text-sm">
                              <p className="font-medium text-gray-900 mb-2">Column Names:</p>
                              <div className="space-y-1">
                                {Object.keys(csvData[0]).slice(0, 6).map(header => (
                                  <div key={header} className="text-xs text-muted-foreground bg-gray-50 px-2 py-1 rounded">
                                    {header}
                                  </div>
                                ))}
                                {Object.keys(csvData[0]).length > 6 && (
                                  <div className="text-xs text-muted-foreground">
                                    +{Object.keys(csvData[0]).length - 6} more columns
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="border-t pt-4 text-center">
                        <Button variant="continue" size="sm" className="w-full" onClick={handleContinue}>
                          Continue
                        </Button>
                      </div>
                    </div>
                  ) : currentStep === 'mapping' ? (
                    /* Column Mapping Validation Panel */
                    <div className="space-y-4">
                      <div className="text-center">
                        <div className="flex justify-center mb-2">
                          <svg
                            className="w-8 h-8 text-primary"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <p className="font-medium text-gray-900 mb-1">
                          Column Mapping
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Verify column mappings are correct
                        </p>
                      </div>

                      <div className="border-t pt-4">
                        <div className="space-y-2 text-sm">
                          <div className="font-medium">Required Fields:</div>
                          {requiredFields.map(field => (
                            <div key={field} className="flex items-center gap-2">
                              {isMapped(field) ? (
                                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                              )}
                              <span className={isMapped(field) ? 'text-muted-foreground' : 'text-amber-600'}>
                                {field.charAt(0).toUpperCase() + field.slice(1)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="border-t pt-4">
                        <Button
                          variant="continue"
                          className="w-full"
                          disabled={!hasRequiredMappings()}
                          onClick={handleContinue}
                        >
                          Continue
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* Record Detail Panel for Learning Step */
                    <div>
                      <div className="flex justify-between items-center -mt-6">
                        <h3 className="text-lg font-semibold text-gray-900">
                          Record {selectedRecordIndex + 1}
                        </h3>
                        <div className="flex items-center gap-2 px-2 py-1 rounded text-xs">
                          {/* Duplicate Detection Indicator */}
                          {recordFingerprints.get(selectedRecordIndex)?.existing && (
                            <span className={`px-2 py-1 rounded flex items-center gap-1 ${
                              recordFingerprints.get(selectedRecordIndex)?.action === 'imported'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-purple-100 text-purple-800'
                            }`}>
                              <AlertTriangle className="h-3 w-3" />
                              Previously {recordFingerprints.get(selectedRecordIndex)?.action === 'imported' ? 'Imported' : 'Disregarded'}
                            </span>
                          )}

                          {/* Current Status */}
                          {recordStatuses[selectedRecordIndex] === 'matched' && (
                            <span className="bg-green-100 text-green-800 px-2 py-1 rounded">Matched</span>
                          )}
                          {recordStatuses[selectedRecordIndex] === 'disregarded' && (
                            <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded">Disregarded</span>
                          )}
                          {!recordStatuses[selectedRecordIndex] && (
                            <span className="bg-amber-100 text-amber-800 px-2 py-1 rounded">Pending</span>
                          )}
                        </div>
                      </div>

                      {filteredCsvData[selectedRecordIndex] && (
                        <div className="space-y-8 mt-4">
                          {/* Smart Mapping Info */}

                          {/* Sales Information */}
                          {(getCurrentRecordData(selectedRecordIndex).date || getCurrentRecordData(selectedRecordIndex).consignment) && (
                            <div className="space-y-2">
                              <h4 className="text-xs font-medium text-muted-foreground uppercase">
                                Sales Information
                              </h4>
                              <div className="space-y-3">
                                {getCurrentRecordData(selectedRecordIndex).sales_id !== undefined && (
                                  <CompactField
                                    label="Sales ID"
                                    value={getCurrentRecordData(selectedRecordIndex).sales_id || ''}
                                    onChange={(value) => handleFieldChange('sales_id', value)}
                                    placeholder="Enter sales ID"
                                  />
                                )}
                                {getCurrentRecordData(selectedRecordIndex).date !== undefined && (
                                  <CompactField
                                    label="Date"
                                    value={getCurrentRecordData(selectedRecordIndex).date || ''}
                                    onChange={(value) => handleFieldChange('date', value)}
                                    placeholder="Enter date"
                                  />
                                )}
                              </div>
                            </div>
                          )}

                          {/* Customer Information */}
                          {getCurrentRecordData(selectedRecordIndex).customer !== undefined && (
                            <div className="space-y-2">
                              <h4
                                className="text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:underline hover:text-gray-600 transition-all duration-200"
                                onClick={() => {
                                  const customerId = recordEdits[selectedRecordIndex]?.customerId
                                  if (customerId) {
                                    const customer = customers.find(c => c.id === customerId)
                                    if (customer) {
                                      setEditingCustomer(customer)
                                      setShowCustomerEditModal(true)
                                    }
                                  }
                                }}
                              >
                                Customer Information
                              </h4>
                              <div className="space-y-3">
                                {/* Customer Selection - Combobox with Search */}
                                <Popover open={customerComboboxOpen} onOpenChange={setCustomerComboboxOpen}>
                                  <PopoverTrigger asChild>
                                    <Button
                                      variant="outline"
                                      role="combobox"
                                      aria-expanded={customerComboboxOpen}
                                      className={`w-full justify-between ${validationErrors[selectedRecordIndex]?.customer ? 'border-2 border-red-500' : ''}`}
                                    >
                                      {recordEdits[selectedRecordIndex]?.customerId
                                        ? customers.find(c => c.id === recordEdits[selectedRecordIndex]?.customerId)?.name
                                        : "Select customer..."}
                                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-full mx-[5px] p-0">
                                    <Command>
                                      <CommandInput placeholder="Search customers..." />
                                      <CommandList>
                                        <CommandEmpty>No customer found.</CommandEmpty>

                                        {/* Add new customer at top */}
                                        <CommandGroup>
                                          <CommandItem
                                            value="add-new-customer"
                                            onSelect={() => {
                                              setModalFormData({
                                                csvValue: getCurrentRecordData(selectedRecordIndex).customer
                                              })
                                              setShowCustomerModal(true)
                                              setCustomerComboboxOpen(false)
                                            }}
                                            className="text-primary"
                                          >
                                            <Plus className="mr-2 h-4 w-4" />
                                            Add new Customer
                                          </CommandItem>
                                        </CommandGroup>

                                        {/* Grouped customers by type */}
                                        {groupedCustomers.map((group) => {
                                          const GroupIcon = group.icon
                                          return (
                                            <CommandGroup key={group.value}>
                                              <div className="flex items-center gap-2 px-2 py-1.5 text-xs font-medium text-muted-foreground" cmdk-group-heading="">
                                                <GroupIcon className="h-4 w-4" />
                                                {group.label}
                                              </div>
                                              {group.customers.map((customer) => (
                                                <CommandItem
                                                  key={customer.id}
                                                  value={`${customer.name}-${customer.id}`}
                                                  onSelect={() => {
                                                    const csvValue = getCurrentRecordData(selectedRecordIndex).customer

                                                    // Mark this record as manually edited (to preserve user choice)
                                                    setManualOverrides(prev => new Set([...prev, selectedRecordIndex]))

                                                    handleFieldChange('customerId', customer.id)
                                                    handleFieldChange('customerName', customer.name)

                                                    // Filter branches for this customer
                                                    setFilteredBranches(prev => ({
                                                      ...prev,
                                                      [selectedRecordIndex]: customer.branches || []
                                                    }))

                                                    // Clear any existing branch selection when customer changes
                                                    handleFieldChange('branchId', null)
                                                    handleFieldChange('branchName', null)

                                                    // For Direct customers, explicitly set branch as not needed
                                                    const customerType = customer.branches?.[0]?.customer_type?.name
                                                    if (customerType === 'direct') {
                                                      handleFieldChange('branchId', null)
                                                      handleFieldChange('branchName', null)
                                                      applySmartMapping(csvValue, customer.id, customer.name, null, null)
                                                    }

                                                    // Clear customer validation error
                                                    setValidationErrors(prev => {
                                                      if (prev[selectedRecordIndex]?.customer) {
                                                        const newErrors = { ...prev }
                                                        if (newErrors[selectedRecordIndex]) {
                                                          delete newErrors[selectedRecordIndex].customer
                                                          if (Object.keys(newErrors[selectedRecordIndex]).length === 0) {
                                                            delete newErrors[selectedRecordIndex]
                                                          }
                                                        }
                                                        return newErrors
                                                      }
                                                      return prev
                                                    })

                                                    setCustomerComboboxOpen(false)
                                                  }}
                                                >
                                                  {customer.name}
                                                  <Check
                                                    className={`ml-auto h-4 w-4 ${
                                                      recordEdits[selectedRecordIndex]?.customerId === customer.id
                                                        ? "opacity-100"
                                                        : "opacity-0"
                                                    }`}
                                                  />
                                                </CommandItem>
                                              ))}
                                            </CommandGroup>
                                          )
                                        })}
                                      </CommandList>
                                    </Command>
                                  </PopoverContent>
                                </Popover>


                                {/* Branch Selection */}
                                <Select
                                  value={recordEdits[selectedRecordIndex]?.branchId === null ? 'no-branch' : recordEdits[selectedRecordIndex]?.branchId || 'no-branch'}
                                  onValueChange={(value) => {
                                    if (value === 'no-branch') {
                                      const csvValue = getCurrentRecordData(selectedRecordIndex).customer
                                      const customerId = recordEdits[selectedRecordIndex]?.customerId
                                      const customerName = recordEdits[selectedRecordIndex]?.customerName

                                      // Mark this record as manually edited (to preserve user choice)
                                      setManualOverrides(prev => new Set([...prev, selectedRecordIndex]))

                                      handleFieldChange('branchId', null)
                                      handleFieldChange('branchName', null)

                                      // Apply smart mapping with no branch
                                      if (customerId && csvValue) {
                                        applySmartMapping(csvValue, customerId, customerName, null, null)
                                      }

                                      // Clear branch validation error
                                      setValidationErrors(prev => {
                                        if (prev[selectedRecordIndex]?.branch) {
                                          const newErrors = { ...prev }
                                          if (newErrors[selectedRecordIndex]) {
                                            delete newErrors[selectedRecordIndex].branch
                                            if (Object.keys(newErrors[selectedRecordIndex]).length === 0) {
                                              delete newErrors[selectedRecordIndex]
                                            }
                                          }
                                          return newErrors
                                        }
                                        return prev
                                      })
                                    } else if (value === 'add-new-branch') {
                                      // Check if customer is selected
                                      if (!recordEdits[selectedRecordIndex]?.customerId) {
                                        setError('Please select a customer first before adding a branch')
                                        return
                                      }
                                      setModalFormData({
                                        csvValue: getCurrentRecordData(selectedRecordIndex).branch || getCurrentRecordData(selectedRecordIndex).customer,
                                        customerId: recordEdits[selectedRecordIndex]?.customerId
                                      })
                                      setShowBranchModal(true)
                                    } else {
                                      const branch = filteredBranches[selectedRecordIndex]?.find(b => b.id === value)
                                      const csvValue = getCurrentRecordData(selectedRecordIndex).customer
                                      const customerId = recordEdits[selectedRecordIndex]?.customerId
                                      const customerName = recordEdits[selectedRecordIndex]?.customerName

                                      // Mark this record as manually edited (to preserve user choice)
                                      setManualOverrides(prev => new Set([...prev, selectedRecordIndex]))

                                      handleFieldChange('branchId', value)
                                      handleFieldChange('branchName', branch?.name || '')

                                      // Apply smart mapping with both customer and branch
                                      if (customerId && csvValue) {
                                        applySmartMapping(csvValue, customerId, customerName, value, branch?.name || '')
                                      }

                                      // Clear branch validation error
                                      setValidationErrors(prev => {
                                        if (prev[selectedRecordIndex]?.branch) {
                                          const newErrors = { ...prev }
                                          if (newErrors[selectedRecordIndex]) {
                                            delete newErrors[selectedRecordIndex].branch
                                            if (Object.keys(newErrors[selectedRecordIndex]).length === 0) {
                                              delete newErrors[selectedRecordIndex]
                                            }
                                          }
                                          return newErrors
                                        }
                                        return prev
                                      })
                                    }
                                  }}
                                  disabled={(() => {
                                    const selectedCustomer = customers.find(c => c.id === recordEdits[selectedRecordIndex]?.customerId)
                                    const isDirectCustomer = selectedCustomer?.type === 'direct'
                                    return isDirectCustomer || (!recordEdits[selectedRecordIndex]?.customerId && filteredBranches[selectedRecordIndex]?.length === 0)
                                  })()}
                                >
                                  <SelectTriggerMinimal className={validationErrors[selectedRecordIndex]?.branch ? 'border-2 border-red-500' : ''}>
                                    <SelectValue placeholder={(() => {
                                      if (!recordEdits[selectedRecordIndex]?.customerId) return "Select customer first..."
                                      const selectedCustomer = customers.find(c => c.id === recordEdits[selectedRecordIndex]?.customerId)
                                      const isDirectCustomer = selectedCustomer?.type === 'direct'
                                      return isDirectCustomer ? "N/A - Direct Customer" : "Select branch..."
                                    })()} />
                                  </SelectTriggerMinimal>
                                  <SelectContent>
                                    <SelectItem value="no-branch">
                                      Main / No Branch
                                    </SelectItem>
                                    {filteredBranches[selectedRecordIndex]?.map(branch => (
                                      <SelectItem key={branch.id} value={branch.id}>
                                        {branch.name}
                                      </SelectItem>
                                    ))}
                                    {(() => {
                                      const selectedCustomer = customers.find(c => c.id === recordEdits[selectedRecordIndex]?.customerId)
                                      const isDirectCustomer = selectedCustomer?.type === 'direct'
                                      return recordEdits[selectedRecordIndex]?.customerId && !isDirectCustomer
                                    })() && (
                                      <SelectItem value="add-new-branch">
                                        <div className="flex items-center gap-2 text-primary">
                                          <Plus className="w-4 h-4" />
                                          Add new Branch
                                        </div>
                                      </SelectItem>
                                    )}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          )}

                          {/* Product Information */}
                          {(getCurrentRecordData(selectedRecordIndex).product !== undefined || getCurrentRecordData(selectedRecordIndex).strain !== undefined || getCurrentRecordData(selectedRecordIndex).quantity !== undefined || getCurrentRecordData(selectedRecordIndex).price !== undefined) && (
                            <div className="space-y-2">
                              <h4 className="text-xs font-medium text-muted-foreground uppercase">
                                Product Information
                              </h4>
                              <div className="space-y-3">
                                {getCurrentRecordData(selectedRecordIndex).product !== undefined && (
                                  <CompactField
                                    label="Product"
                                    value={getCurrentRecordData(selectedRecordIndex).product || ''}
                                    onChange={(value) => handleFieldChange('product', value)}
                                    placeholder="Enter product"
                                  />
                                )}
                                {/* Type and Strain - conditional layout */}
                                {getCurrentRecordData(selectedRecordIndex).type !== undefined && getCurrentRecordData(selectedRecordIndex).strain !== undefined && (
                                  <div className="grid grid-cols-2 gap-2">
                                    <CompactField
                                      label="Type"
                                      value={getCurrentRecordData(selectedRecordIndex).type || ''}
                                      onChange={(value) => handleFieldChange('type', value)}
                                      placeholder="Enter type"
                                    />
                                    <CompactField
                                      label="Strain"
                                      value={getCurrentRecordData(selectedRecordIndex).strain || ''}
                                      onChange={(value) => handleFieldChange('strain', value)}
                                      placeholder="Enter strain"
                                    />
                                  </div>
                                )}
                                {getCurrentRecordData(selectedRecordIndex).type !== undefined && getCurrentRecordData(selectedRecordIndex).strain === undefined && (
                                  <CompactField
                                    label="Type"
                                    value={getCurrentRecordData(selectedRecordIndex).type || ''}
                                    onChange={(value) => handleFieldChange('type', value)}
                                    placeholder="Enter type"
                                  />
                                )}
                                {getCurrentRecordData(selectedRecordIndex).strain !== undefined && getCurrentRecordData(selectedRecordIndex).type === undefined && (
                                  <CompactField
                                    label="Strain"
                                    value={getCurrentRecordData(selectedRecordIndex).strain || ''}
                                    onChange={(value) => handleFieldChange('strain', value)}
                                    placeholder="Enter strain"
                                  />
                                )}
                                {(getCurrentRecordData(selectedRecordIndex).quantity !== undefined || getCurrentRecordData(selectedRecordIndex).price !== undefined) && (
                                  <div className="grid grid-cols-2 gap-2">
                                    {getCurrentRecordData(selectedRecordIndex).quantity !== undefined && (
                                      <CompactField
                                        label="Quantity"
                                        value={getCurrentRecordData(selectedRecordIndex).quantity || ''}
                                        onChange={(value) => handleFieldChange('quantity', value)}
                                        placeholder="Enter quantity"
                                      />
                                    )}
                                    {getCurrentRecordData(selectedRecordIndex).price !== undefined && (
                                      <CompactField
                                        label="Price"
                                        value={getCurrentRecordData(selectedRecordIndex).price || ''}
                                        onChange={(value) => handleFieldChange('price', value)}
                                        placeholder="Enter price"
                                      />
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Action Buttons and Navigation Container */}
                          <div>
                            {/* Action Buttons */}
                            <div className="flex flex-col gap-2 py-3 border-t">
                              <Button
                                variant="outline"
                                size="sm"
                                className={`w-full transition-colors ${
                                  recordStatuses[selectedRecordIndex] === 'matched'
                                    ? 'bg-green-100 border-green-200 text-green-700 hover:bg-green-150 hover:border-green-300'
                                    : 'border-2 border-green-600 text-black bg-white hover:bg-green-50'
                                } ${shakeButton ? 'animate-shake' : ''}`}
                                onClick={() => handleMatch(selectedRecordIndex)}
                              >
                                {recordStatuses[selectedRecordIndex] === 'matched' ? 'Unmatch' : 'Match This Record'}
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className={`w-full transition-colors ${
                                  !isReadyForFindAndMatchAll(selectedRecordIndex)
                                    ? 'bg-green-100 border-green-200 text-green-600 opacity-50 cursor-not-allowed'
                                    : 'bg-green-600 border-green-600 text-white hover:bg-green-700 hover:border-green-700'
                                }`}
                                onClick={() => handleFindAndMatchWithConfirmation(selectedRecordIndex, 'all')}
                                disabled={!isReadyForFindAndMatchAll(selectedRecordIndex)}
                              >
                                Match Similar
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                className={`w-full transition-colors ${
                                  recordStatuses[selectedRecordIndex] === 'disregarded'
                                    ? 'bg-red-600 border-red-600 text-white hover:bg-red-700 hover:border-red-700'
                                    : 'border-2 border-red-600 text-black bg-white hover:bg-red-50'
                                }`}
                                onClick={() => handleDisregard(selectedRecordIndex)}
                              >
                                {recordStatuses[selectedRecordIndex] === 'disregarded' ? 'Undo Disregard' : 'Disregard'}
                              </Button>
                            </div>

                            {/* Navigation */}
                            <div className="border-t py-3">
                              <div className="flex justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigateRecord('first')}
                                disabled={displayedToOriginalIndex.indexOf(selectedRecordIndex) === 0}
                                className="px-2"
                              >
                                <ChevronFirst className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigateRecord('prev')}
                                disabled={displayedToOriginalIndex.indexOf(selectedRecordIndex) === 0}
                                className="px-2"
                              >
                                <ChevronLeft className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigateRecord('next')}
                                disabled={displayedToOriginalIndex.indexOf(selectedRecordIndex) === displayedRecords.length - 1}
                                className="px-2"
                              >
                                <ChevronRight className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => navigateRecord('last')}
                                disabled={displayedToOriginalIndex.indexOf(selectedRecordIndex) === displayedRecords.length - 1}
                                className="px-2"
                              >
                                <ChevronLast className="h-4 w-4" />
                              </Button>
                              </div>
                            </div>
                            <div className="text-xs text-muted-foreground text-center mt-1">
                              {displayedToOriginalIndex.indexOf(selectedRecordIndex) + 1} / {displayedRecords.length}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Customer Creation Modal - Optimized with isolated state */}
      <Dialog open={showCustomerModal} onOpenChange={setShowCustomerModal}>
        <DialogContent className="odin-modal-small max-w-md">
          <CustomerModalForm
            csvValue={modalFormData.csvValue}
            onSubmit={handleCreateCustomer}
            onCancel={() => setShowCustomerModal(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Branch Creation Modal */}
      <Dialog open={showBranchModal} onOpenChange={setShowBranchModal}>
        <DialogContent className="odin-modal-medium max-w-lg">
          <BranchModalForm
            csvValue={modalFormData.csvValue}
            customerId={modalFormData.customerId}
            onSubmit={handleCreateBranch}
            onCancel={() => setShowBranchModal(false)}
          />
        </DialogContent>
      </Dialog>


      {/* Product Type Creation Modal (Minimal for Import) */}
      <Dialog open={showProductTypeModal} onOpenChange={setShowProductTypeModal}>
        <DialogContent className="odin-modal-small">
          <DialogHeader>
            <DialogTitle>Add New Product Type</DialogTitle>
            <DialogDescription>
              Create a new product type for CSV value: "{modalFormData.csvValue}"
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Product Type Name *</Label>
              <Input
                value={modalFormData.productTypeName || modalFormData.csvValue || ''}
                onChange={(e) => updateModalField('productTypeName')(e.target.value)}
                placeholder="e.g., A Class, B Class, Gift, Sample"
              />
            </div>
            <div className="text-sm text-muted-foreground">
              <p>Default settings will be applied:</p>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>No discount (0%) - you can adjust this later in Product Dashboard</li>
                <li>Active status</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProductTypeModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateProductType}>
              Create Product Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Import Summary Modal */}
      <Dialog open={showImportSummaryModal} onOpenChange={setShowImportSummaryModal}>
        <DialogContent className="odin-modal-medium">
          <DialogHeader>
            <DialogTitle>Import Summary</DialogTitle>
            <DialogDescription>
              Review your matching progress and proceed with import
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {(() => {
              const matchedCount = Object.values(recordStatuses).filter(status => status === 'matched').length
              const disregardedCount = Object.values(recordStatuses).filter(status => status === 'disregarded').length
              const unmatchedCount = filteredCsvData.length - matchedCount - disregardedCount

              return (
                <div className="space-y-4">
                  <div className="grid grid-cols-3 gap-4">
                    {/* Total Records */}
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <div className="text-2xl font-bold text-gray-900">{filteredCsvData.length}</div>
                      <div className="text-sm text-gray-600">Total Records</div>
                    </div>

                    {/* Matched Records */}
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <div className="text-2xl font-bold text-green-700">{matchedCount}</div>
                      <div className="text-sm text-green-600">Matched</div>
                    </div>

                    {/* Disregarded Records */}
                    <div className="text-center p-4 bg-red-50 rounded-lg">
                      <div className="text-2xl font-bold text-red-700">{disregardedCount}</div>
                      <div className="text-sm text-red-600">Disregarded</div>
                    </div>
                  </div>

                  {unmatchedCount > 0 && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-amber-800">
                            {unmatchedCount} records remain unmatched
                          </div>
                          <div className="text-sm text-amber-600 mt-1">
                            These records will not be imported unless matched or you can disregard them all.
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDisregardAllUnmatched}
                          className="border-amber-300 text-amber-700 hover:bg-amber-100"
                        >
                          Disregard All Unmatched
                        </Button>
                      </div>
                    </div>
                  )}

                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <div className="font-medium text-blue-800 mb-2">
                      Ready to Import
                    </div>
                    <div className="text-sm text-blue-600">
                      {matchedCount} record{matchedCount !== 1 ? 's' : ''} will be imported to your database
                    </div>
                  </div>
                </div>
              )
            })()}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportSummaryModal(false)}>
              Back to Matching
            </Button>
            <Button onClick={handleProceedFromSummary}>
              Continue to Product Configuration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Find & Match All Confirmation Dialog */}
      <Dialog open={showMatchConfirmDialog} onOpenChange={setShowMatchConfirmDialog}>
        <DialogContent className="odin-modal-small">
          <DialogHeader>
            <DialogTitle>
              {pendingMatchData.conflictedRecords?.length > 0 ? '⚠️ Override Protection' : '🎉 Great Match Found!'}
            </DialogTitle>
            <DialogDescription>
              {pendingMatchData.conflictedRecords?.length > 0 ? (
                <>
                  Found {pendingMatchData.matchCount} matching record{pendingMatchData.matchCount !== 1 ? 's' : ''}, but {pendingMatchData.conflictedRecords.length} have manual override{pendingMatchData.conflictedRecords.length !== 1 ? 's' : ''} that will be replaced.
                </>
              ) : (
                <>
                  Excellent! We found {pendingMatchData.matchCount} identical record{pendingMatchData.matchCount !== 1 ? 's' : ''} ready to be matched instantly.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {pendingMatchData.conflictedRecords?.length > 0 ? (
              <div className="space-y-3">
                <p className="text-sm text-amber-700 bg-amber-50 p-3 rounded border border-amber-200">
                  <strong>Manual overrides detected:</strong> {pendingMatchData.conflictedRecords.length} record{pendingMatchData.conflictedRecords.length !== 1 ? 's' : ''} have been manually configured and will be overwritten.
                </p>
                <p className="text-sm text-muted-foreground">
                  Safe records: {pendingMatchData.safeRecords.length}
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground mb-3">
                  This will automatically apply your selections to all matching records, saving you time!
                </p>
                <p className="text-sm font-medium text-green-700">
                  Only {Math.max(0, filteredCsvData.length - Object.keys(recordStatuses).length - pendingMatchData.matchCount - 1)} record{Math.max(0, filteredCsvData.length - Object.keys(recordStatuses).length - pendingMatchData.matchCount - 1) !== 1 ? 's' : ''} remaining after this! 🙌
                </p>
              </>
            )}
          </div>

          <DialogFooter>
            {pendingMatchData.conflictedRecords?.length > 0 ? (
              <div className="flex flex-col w-full gap-2">
                <div className="flex gap-2 justify-end">
                  <Button
                    onClick={confirmFindAndMatchOverwriteAll}
                    className="order-first"
                  >
                    Overwrite All ({pendingMatchData.matchCount})
                  </Button>
                  <Button
                    variant="outline"
                    onClick={confirmFindAndMatchThisRecordOnly}
                  >
                    This Record Only
                  </Button>
                </div>
                <Button
                  variant="ghost"
                  onClick={cancelFindAndMatch}
                  className="w-full"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <>
                <Button
                  onClick={confirmFindAndMatch}
                  className="order-first"
                >
                  Confirm
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowMatchConfirmDialog(false)}
                  autoFocus
                >
                  Back
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Hierarchical Product Configuration Modal */}
      {showProductConfigModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold">Configure Products for Import</h2>
                <p className="text-sm text-gray-600">Products grouped by mushroom strain and quality grade</p>
              </div>
              <Button variant="ghost" onClick={() => setShowProductConfigModal(false)}>
                ✕
              </Button>
            </div>
            <div className="flex-1 overflow-auto p-4">
              <ReviewViewPrototype
                products={uniqueProducts.map(p => ({
                  Strain: p.strain,
                  'Product Type': p.productType,
                  recordCount: p.recordCount
                }))}
              />
            </div>
            <div className="p-4 border-t flex items-center justify-end gap-2">
              <Button variant="outline" onClick={() => setShowProductConfigModal(false)}>
                Cancel
              </Button>
              <Button onClick={handleConfirmProductConfiguration}>
                Confirm & Proceed to Import
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Product Type Matching Modal */}
      <ProductTypeMatchModal
        open={showProductTypeMatchModal}
        onClose={() => setShowProductTypeMatchModal(false)}
        onConfirm={handleConfirmProductTypes}
        onCreateType={createProductType}
        uniqueTypes={uniqueProductTypes}
        existingTypes={existingProductTypes}
        initialMappings={productTypeMappings}
      />

      {/* Product Strain Matching Modal */}
      <ProductStrainMatchModal
        open={showProductStrainMatchModal}
        onClose={() => setShowProductStrainMatchModal(false)}
        onConfirm={handleConfirmStrains}
        uniqueStrains={uniqueProductStrains}
        existingProducts={existingProducts}
        initialMappings={productStrainMappings}
      />

      {/* Import Preview Modal */}
      <ImportWizardV2PreviewModal
        open={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        csvData={filteredCsvData}
        recordStatuses={recordStatuses}
        customerLookup={customerLookup}
        productConfigs={productConfigs}
        productMatches={productMatches}
        getCurrentRecordData={getCurrentRecordData}
        onConfirm={handleConfirmPreview}
        importing={importProgress.current > 0 && !importProgress.completed}
      />

      {/* Resume Session Loading Modal */}
      <Dialog open={isResuming} onOpenChange={() => {}}>
        <DialogContent className="odin-modal-small" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Resuming Import Session</DialogTitle>
            <DialogDescription>
              Loading your saved progress...
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            <p className="text-sm text-gray-500">This may take a few moments</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Loading Product Data Modal */}
      <Dialog open={isLoadingProductData} onOpenChange={() => {}}>
        <DialogContent className="odin-modal-small" showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Loading Product Data</DialogTitle>
            <DialogDescription>
              Fetching products and types from database...
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            <p className="text-sm text-gray-500">Preparing product configurator</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Progress Modal */}
      <Dialog open={showImportProgressModal} onOpenChange={() => {
        // Allow closing if import is completed or if progress shows 100%
        if (importProgress.completed || (importProgress.current >= importProgress.total && importProgress.total > 0)) {
          setShowImportProgressModal(false)
        }
      }}>
        <DialogContent className="odin-modal-medium">
          <DialogHeader>
            <DialogTitle>Importing Data</DialogTitle>
            <DialogDescription>
              {importProgress.completed
                ? 'Import completed successfully!'
                : 'Please wait while your data is being imported...'
              }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-6">
            {/* Progress Bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Progress</span>
                <span>{importProgress.current} / {importProgress.total} records</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-green-600 h-3 rounded-full transition-all duration-300 ease-out"
                  style={{
                    width: importProgress.total > 0
                      ? `${(importProgress.current / importProgress.total) * 100}%`
                      : '0%'
                  }}
                />
              </div>
              <div className="text-center text-sm text-muted-foreground">
                {importProgress.total > 0
                  ? `${Math.round((importProgress.current / importProgress.total) * 100)}% complete`
                  : 'Preparing import...'
                }
              </div>
            </div>

            {/* Status Messages */}
            <div className="text-center">
              {importProgress.error ? (
                <div className="flex flex-col items-center gap-2 text-red-700">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <div>
                    <div className="font-medium">Import Failed</div>
                    <div className="text-sm text-red-600 mt-1">{importProgress.error}</div>
                  </div>
                </div>
              ) : !importProgress.completed ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600" />
                  <span className="text-sm text-muted-foreground">
                    Processing record {importProgress.current + 1} of {importProgress.total}...
                  </span>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2 text-green-700">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="font-medium">
                    Successfully imported {importProgress.total} records!
                  </span>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            {importProgress.error ? (
              <Button
                variant="outline"
                onClick={() => {
                  setShowImportProgressModal(false)
                  setImportProgress({ current: 0, total: 0, completed: false, error: null })
                }}
                className="w-full"
              >
                Close
              </Button>
            ) : importProgress.completed ? (
              <Button
                onClick={() => {
                  setShowImportProgressModal(false)
                  navigate('/sales')  // Redirect to sales dashboard after successful import
                }}
                className="w-full"
              >
                Finish & View Sales
              </Button>
            ) : !importProgress.completed && importProgress.current >= importProgress.total && importProgress.total > 0 ? (
              <Button
                variant="outline"
                onClick={() => {
                  setShowImportProgressModal(false)
                }}
                className="w-full"
              >
                Close
              </Button>
            ) : null}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Session Recovery Dialog */}
      <Dialog open={showRecoveryDialog} onOpenChange={setShowRecoveryDialog}>
        <DialogContent className="odin-modal-medium">
          <DialogHeader>
            <DialogTitle>Resume Import Session?</DialogTitle>
            <DialogDescription>
              We found {pendingSessions.length} incomplete import session{pendingSessions.length > 1 ? 's' : ''} that you can resume or start fresh.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-6">
            {pendingSessions.map((session) => {
              const sessionSummary = getSessionStateSummary(session.session_state || {})
              const hasValidState = sessionSummary.valid

              return (
                <div key={session.id} className="border rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-medium">{session.file_name}</div>
                      <div className="text-sm text-gray-600 mt-1">
                        {session.total_rows} rows • Status: <span className="capitalize">{session.status}</span>
                      </div>
                      {hasValidState && (
                        <div className="mt-2 flex gap-3 text-xs">
                          <div className="flex items-center gap-1">
                            <Check className="h-3 w-3 text-green-600" />
                            <span>{sessionSummary.matchedRecords} matched</span>
                          </div>
                          {sessionSummary.unmatchedRecords > 0 && (
                            <div className="flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3 text-yellow-600" />
                              <span>{sessionSummary.unmatchedRecords} unmatched</span>
                            </div>
                          )}
                          {sessionSummary.productVariants > 0 && (
                            <div className="flex items-center gap-1">
                              <Package className="h-3 w-3 text-blue-600" />
                              <span>{sessionSummary.productVariants} products</span>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="text-xs text-gray-500 mt-1">
                        {hasValidState && sessionSummary.savedAt
                          ? `Last saved: ${new Date(sessionSummary.savedAt).toLocaleString()}`
                          : `Started: ${new Date(session.created_at).toLocaleString()}`
                        }
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleResumeSession(session.id)}
                      disabled={['completed', 'failed', 'abandoned'].includes(session.status)}
                    >
                      Resume
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>

          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={handleAbandonSessions}>
              Clear All Sessions
            </Button>
            <Button onClick={handleStartNewSession}>
              Start New Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Selection Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="odin-modal-medium">
          <DialogHeader>
            <DialogTitle>Import Template Found</DialogTitle>
            <DialogDescription>
              We found a template that matches this CSV structure. Would you like to use it?
            </DialogDescription>
          </DialogHeader>

          {matchedTemplate && (
            <div className="space-y-4 py-6">
              <div className="border rounded-lg p-4 bg-gray-50 space-y-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="font-semibold text-lg">{matchedTemplate.name}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      Last used: {new Date(matchedTemplate.last_used).toLocaleDateString()}
                    </div>
                  </div>
                </div>

                {/* Template Details */}
                <div className="mt-4 space-y-2">
                  {matchedTemplate.column_mappings && Object.keys(matchedTemplate.column_mappings).length > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-600" />
                      <span>Column mappings: {Object.keys(matchedTemplate.column_mappings).length} fields</span>
                    </div>
                  )}
                  {matchedTemplate.smart_mappings && Object.keys(matchedTemplate.smart_mappings).length > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-600" />
                      <span>Smart mappings: {Object.keys(matchedTemplate.smart_mappings).length} customer/branch rules</span>
                    </div>
                  )}
                  {matchedTemplate.product_mappings && Object.keys(matchedTemplate.product_mappings).length > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="h-4 w-4 text-green-600" />
                      <span>Product mappings: {Object.keys(matchedTemplate.product_mappings).length} products</span>
                    </div>
                  )}
                </div>

                <Alert className="mt-4">
                  <AlertDescription className="text-sm">
                    Using this template will automatically apply column mappings, customer/branch rules, and product configurations from your previous import.
                  </AlertDescription>
                </Alert>
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowTemplateDialog(false)
                setTemplateToApply(null)
              }}
            >
              Skip Template
            </Button>
            <Button onClick={() => {
              if (matchedTemplate) {
                applyTemplate(matchedTemplate)
                setShowTemplateDialog(false)
              }
            }}>
              Use Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Template Manager Dialog */}
      <Dialog open={showTemplateManager} onOpenChange={setShowTemplateManager}>
        <DialogContent className="odin-modal-large">
          <DialogHeader>
            <DialogTitle>Import Templates</DialogTitle>
            <DialogDescription>
              Manage your import templates. Templates automatically apply column mappings and smart rules to matching CSV files.
            </DialogDescription>
          </DialogHeader>

          <div className="py-6">
            {userTemplates.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No templates found.</p>
                <p className="text-sm mt-2">Templates are created automatically when you complete an import.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {userTemplates.map((template) => (
                  <div key={template.id} className="border rounded-lg p-4 space-y-3 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        {/* Template Name */}
                        {renamingTemplateId === template.id ? (
                          <div className="flex items-center gap-2">
                            <Input
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              placeholder="Template name"
                              className="flex-1"
                              autoFocus
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleRenameTemplate(template.id, renameValue)
                                } else if (e.key === 'Escape') {
                                  setRenamingTemplateId(null)
                                  setRenameValue('')
                                }
                              }}
                            />
                            <Button
                              size="sm"
                              onClick={() => handleRenameTemplate(template.id, renameValue)}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setRenamingTemplateId(null)
                                setRenameValue('')
                              }}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="font-semibold text-lg">{template.name}</div>
                        )}

                        <div className="text-sm text-gray-600 mt-1">
                          Last used: {new Date(template.last_used).toLocaleDateString()}
                        </div>
                      </div>

                      {/* Actions */}
                      {renamingTemplateId !== template.id && (
                        <div className="flex items-center gap-2">
                          {csvHeaders.length > 0 && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                applyTemplate(template)
                                setShowTemplateManager(false)
                              }}
                            >
                              Apply
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setRenamingTemplateId(template.id)
                              setRenameValue(template.name)
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteTemplate(template.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Template Details */}
                    <div className="flex gap-4 text-sm text-gray-600">
                      {template.column_mappings && Object.keys(template.column_mappings).length > 0 && (
                        <div className="flex items-center gap-1">
                          <Check className="h-3 w-3 text-green-600" />
                          <span>{Object.keys(template.column_mappings).length} column mappings</span>
                        </div>
                      )}
                      {template.smart_mappings && Object.keys(template.smart_mappings).length > 0 && (
                        <div className="flex items-center gap-1">
                          <Check className="h-3 w-3 text-green-600" />
                          <span>{Object.keys(template.smart_mappings).length} smart rules</span>
                        </div>
                      )}
                      {template.product_mappings && Object.keys(template.product_mappings).length > 0 && (
                        <div className="flex items-center gap-1">
                          <Check className="h-3 w-3 text-green-600" />
                          <span>{Object.keys(template.product_mappings).length} product mappings</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplateManager(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Imports Manager Dialog */}
      <Dialog open={showImportsManager} onOpenChange={setShowImportsManager}>
        <DialogContent className="odin-modal-large">
          <DialogHeader>
            <DialogTitle>Import History</DialogTitle>
            <DialogDescription>
              View previous import sessions and re-import them with duplicate detection.
            </DialogDescription>
          </DialogHeader>

          <div className="py-6">
            {loadingImports ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
              </div>
            ) : importSessions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No import sessions found.</p>
                <p className="text-sm mt-2">Complete an import to see it here.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {importSessions.map((session) => (
                  <div key={session.id} className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <div className="font-semibold text-lg">
                            {session.file_name}
                            {session.parent_session_id && (
                              <span className="ml-2 text-sm text-gray-500 font-normal">(re-import)</span>
                            )}
                          </div>
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600 font-mono">
                            ID: {session.id.substring(0, 8).toUpperCase()}
                          </code>
                        </div>
                        <div className="text-sm text-gray-600 mt-1 space-y-1">
                          <div>Imported: {new Date(session.created_at).toLocaleDateString()} at {new Date(session.created_at).toLocaleTimeString()}</div>
                          {session.completed_at && (
                            <div>Completed: {new Date(session.completed_at).toLocaleDateString()} at {new Date(session.completed_at).toLocaleTimeString()}</div>
                          )}
                          <div>Records: {session.stagingRecordCount} ({session.valid_rows || 0} valid)</div>
                          <div className="flex items-center gap-2">
                            <span>Status:</span>
                            <span className={`px-2 py-0.5 text-xs rounded-full ${
                              session.status === 'completed' ? 'bg-green-100 text-green-800' :
                              session.status === 'failed' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {session.status}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewSessionDetail(session)}
                        >
                          View Details
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleInitiateReimport(session)}
                        >
                          Re-Import
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowImportsManager(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Re-Import Confirmation Dialog */}
      <Dialog open={reImportDialogOpen} onOpenChange={setReImportDialogOpen}>
        <DialogContent className="odin-modal-medium">
          <DialogHeader>
            <DialogTitle>Re-Import Confirmation</DialogTitle>
            <DialogDescription>
              Re-import this session with duplicate detection.
            </DialogDescription>
          </DialogHeader>

          {selectedSessionForReimport && (
            <div className="py-6 space-y-4">
              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="font-semibold">{selectedSessionForReimport.file_name}</div>
                <div className="text-sm text-gray-600 mt-1">
                  {selectedSessionForReimport.stagingRecordCount} records available for re-import
                </div>
              </div>

              <Alert>
                <AlertTitle>Duplicate Detection Enabled</AlertTitle>
                <AlertDescription>
                  Records that have already been imported will be detected and can be skipped.
                  This prevents creating duplicate sales in your system.
                </AlertDescription>
              </Alert>

              <div className="space-y-2">
                <div className="text-sm font-medium">What would you like to do?</div>
                <div className="text-sm text-gray-600">
                  We recommend skipping duplicates to avoid creating duplicate sales records.
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setReImportDialogOpen(false)
                setSelectedSessionForReimport(null)
              }}
            >
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExecuteReimport(false)}
              disabled={isReimporting}
            >
              {isReimporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Import All (Including Duplicates)
            </Button>
            <Button
              onClick={() => handleExecuteReimport(true)}
              disabled={isReimporting}
            >
              {isReimporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Skip Duplicates (Recommended)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delta Summary Dialog (Duplicate Detection) */}
      <Dialog open={showDeltaSummaryDialog} onOpenChange={setShowDeltaSummaryDialog}>
        <DialogContent className="odin-modal-medium">
          <DialogHeader>
            <DialogTitle>Duplicate Records Detected</DialogTitle>
            <DialogDescription>
              Some records in this import have been previously processed.
            </DialogDescription>
          </DialogHeader>

          {duplicateDetectionSummary && (
            <div className="py-6 space-y-4">
              {/* Summary Stats */}
              <div className="grid grid-cols-3 gap-4">
                <div className="border rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {duplicateDetectionSummary.newRecords}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">New Records</div>
                </div>
                <div className="border rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {duplicateDetectionSummary.alreadyImported}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Already Imported</div>
                </div>
                <div className="border rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {duplicateDetectionSummary.alreadyDisregarded}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Already Disregarded</div>
                </div>
              </div>

              {/* Information Alert */}
              <Alert>
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>What does this mean?</AlertTitle>
                <AlertDescription className="space-y-2 mt-2">
                  <p>
                    <strong>New Records:</strong> Records that haven't been imported before. You'll need to process these.
                  </p>
                  <p>
                    <strong>Already Imported:</strong> These exact records were imported in a previous session.
                    Importing them again will create duplicates in your database.
                  </p>
                  <p>
                    <strong>Already Disregarded:</strong> You previously marked these records to skip.
                    You can import them this time if needed.
                  </p>
                </AlertDescription>
              </Alert>

              {/* Recommendations */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <h4 className="font-semibold text-gray-900">Recommendations:</h4>
                <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
                  <li>Focus on processing the <strong>{duplicateDetectionSummary.newRecords} new records</strong></li>
                  <li>Previously imported records are marked with a blue "Previously Imported" badge</li>
                  <li>You can still import duplicates if you need to, but verify first</li>
                  <li>Consider using the "Show only unmatched" toggle to hide completed records</li>
                </ul>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowDeltaSummaryDialog(false)}>
              Continue with Import
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Edit Modal */}
      <CustomerEditModal
        customer={editingCustomer}
        isOpen={showCustomerEditModal}
        onClose={() => {
          setShowCustomerEditModal(false)
          setEditingCustomer(null)
          setModalFormData({})
        }}
        onUpdate={handleUpdateCustomer}
        onDelete={handleDeleteCustomer}
        hasMatchedRecords={editingCustomer ? checkCustomerHasMatches(editingCustomer.id) : false}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="odin-modal-small">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete Customer
            </DialogTitle>
            <DialogDescription>
              {deleteError || 'Are you sure you want to delete this customer? This action cannot be undone.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowDeleteConfirm(false)
              setDeleteError(null)
            }}>
              Cancel
            </Button>
            {!deleteError && (
              <Button
                variant="destructive"
                onClick={() => {
                  setShowDeleteConfirm(false)
                  handleDeleteCustomer(editingCustomer?.id)
                }}
              >
                Delete Customer
              </Button>
            )}
            {deleteError && (
              <Button
                variant="destructive"
                onClick={() => handleDeleteAndUnmatch(editingCustomer?.id)}
              >
                Delete and Unmatch
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* File Upload Loading Modal */}
      <Dialog open={isUploadingFile} onOpenChange={() => {}}>
        <DialogContent className="odin-modal-small" showCloseButton={false}>
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-12 w-12 text-green-600 animate-spin mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Processing CSV File</h3>
            <p className="text-sm text-gray-600 text-center">
              Please wait while we parse your file and detect templates...
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Session Detail Dialog */}
      <Dialog open={showSessionDetail} onOpenChange={setShowSessionDetail}>
        <DialogContent className="odin-modal-large max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import Session Details</DialogTitle>
            <DialogDescription>
              View detailed information about this import session
            </DialogDescription>
          </DialogHeader>

          {selectedSessionDetail && (
            <ImportHistoryDetailView
              sessionId={selectedSessionDetail.id}
              onClose={() => setShowSessionDetail(false)}
            />
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSessionDetail(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageLayout>
  )

  // Handle customer creation - now accepts form data as parameter
  async function handleCreateCustomer(formData) {
    try {
      if (!formData.customerName?.trim()) {
        setError('Customer name is required')
        return
      }

      if (!formData.customerType?.trim()) {
        setError('Customer type is required')
        return
      }

      // Create customer - createCustomer expects companyName field
      const customerData = {
        companyName: formData.customerName,
        email: formData.customerName.toLowerCase().replace(/\s+/g, '') + '@placeholder.com',
        type: formData.customerType,
        payment_terms: '30 days'
      }

      const newCustomer = await createCustomer(customerData)

      // Invalidate customers cache after creation
      invalidateCache('customers')

      // Add to customers list (no branches initially)
      const customerWithBranches = {
        ...newCustomer,
        branches: []
      }
      setCustomers(prev => [...prev, customerWithBranches])

      // Legacy: Entity mappings no longer needed with new customer/branch separation
      // const csvValue = modalFormData.csvValue
      // setEntityMappings(prev => ({
      //   ...prev,
      //   customers: {
      //     ...prev.customers,
      //     [csvValue]: {
      //       customerId: newCustomer.id,
      //       customerName: newCustomer.name,
      //       branchId: newBranch?.id,
      //       branchName: newBranch?.name
      //     }
      //   }
      // }))

      // Mark this record as manually edited (to preserve user choice)
      setManualOverrides(prev => new Set([...prev, selectedRecordIndex]))

      // Update current record
      handleFieldChange('customerId', newCustomer.id)
      handleFieldChange('customerName', newCustomer.name)

      // Set up filtered branches for this customer (empty initially)
      setFilteredBranches(prev => ({
        ...prev,
        [selectedRecordIndex]: []
      }))

      // Clear any branch selection since this is a new customer
      handleFieldChange('branchId', null)
      handleFieldChange('branchName', null)

      // Apply smart mapping for Direct customers (no branch needed)
      const csvValue = modalFormData.csvValue
      if (formData.customerType === 'direct' && csvValue) {
        applySmartMapping(csvValue, newCustomer.id, newCustomer.name, null, null)
      }

      setShowCustomerModal(false)
    } catch (error) {
      console.error('Failed to create customer:', error)
      setError(`Failed to create customer: ${error.message}`)
    }
  }

  // Handle branch creation
  async function handleCreateBranch(formData) {
    try {
      console.log('Creating branch with formData:', formData)

      if (!formData.branchName?.trim()) {
        setError('Branch name is required')
        return
      }

      if (!formData.customerId) {
        setError('Please select a customer first')
        return
      }

      // Create branch - ensure name field is properly set
      const branchData = {
        customer_id: formData.customerId,
        branchName: formData.branchName.trim(), // This needs to match what createCustomerBranch expects
        address: formData.address || '',
        phone: formData.phone || '',
        email: formData.email || '',
        branch_type: 'branch'
      }

      console.log('Sending branchData to createCustomerBranch:', branchData)
      const newBranch = await createCustomerBranch(branchData)

      // Update the customer's branches in the customers list
      setCustomers(prev => prev.map(customer =>
        customer.id === formData.customerId
          ? { ...customer, branches: [...(customer.branches || []), newBranch] }
          : customer
      ))

      // Update filtered branches for current record
      const updatedBranches = [...(filteredBranches[selectedRecordIndex] || []), newBranch]
      setFilteredBranches(prev => ({
        ...prev,
        [selectedRecordIndex]: updatedBranches
      }))

      // Mark this record as manually edited (to preserve user choice)
      setManualOverrides(prev => new Set([...prev, selectedRecordIndex]))

      // Update current record to use the new branch
      handleFieldChange('branchId', newBranch.id)
      handleFieldChange('branchName', newBranch.name)

      // Apply smart mapping with the new branch
      const csvValue = modalFormData.csvValue
      const customerId = formData.customerId
      const customerName = recordEdits[selectedRecordIndex]?.customerName
      if (csvValue && customerId) {
        applySmartMapping(csvValue, customerId, customerName, newBranch.id, newBranch.name)
      }

      console.log('Branch created successfully:', newBranch)
      console.log('Updated filtered branches:', updatedBranches)

      setShowBranchModal(false)
      setModalFormData({})
    } catch (error) {
      console.error('Failed to create branch:', error)
      setError(`Failed to create branch: ${error.message}`)
    }
  }



  // Handle product type creation (minimal for import context)
  async function handleCreateProductType() {
    try {
      if (!modalFormData.productTypeName?.trim()) {
        setError('Product type name is required')
        return
      }

      // Create product type with defaults for import context
      const productTypeData = {
        name: modalFormData.productTypeName.trim(),
        price_modifier_percent: 0, // Default to no discount
        active: true
      }

      const newProductType = await createProductType(productTypeData)

      // Invalidate product types cache after creation
      invalidateCache('productTypes')

      // Update current product's type field
      updateModalField('productType')(newProductType.name)

      setShowProductTypeModal(false)
      setModalFormData({})
    } catch (error) {
      console.error('Failed to create product type:', error)
      setError(`Failed to create product type: ${error.message}`)
    }
  }

  async function handleFileUpload(file) {
    setError(null)
    setIsUploadingFile(true)

    // File size validation (10MB limit)
    const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
    if (file.size > MAX_FILE_SIZE) {
      setError('File size too large. Maximum file size is 10MB.')
      setIsUploadingFile(false)
      return
    }

    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        const text = e.target.result
        const lines = text.split('\n').filter(line => line.trim())

        if (lines.length === 0) {
          setError('The CSV file appears to be empty')
          setIsUploadingFile(false)
          return
        }

        const headers = lines[0].split(',').map(header => header.trim().replace(/"/g, ''))
        const data = lines.slice(1).map(line => {
          const values = line.split(',').map(value => value.trim().replace(/"/g, ''))
          const row = {}
          headers.forEach((header, index) => {
            row[header] = values[index] || ''
          })
          return row
        })

        // Create import session
        try {
          const sessionData = {
            user_id: user.id,
            file_name: file.name,
            file_size: file.size,
            total_rows: data.length,
            entity_type: 'sales',
            status: 'parsing',
            column_mappings: {},
            import_settings: {
              file_type: file.type || 'text/csv',
              uploaded_at: new Date().toISOString()
            }
          }

          const session = await createImportSession(sessionData)
          setImportSessionId(session.id)
          setSessionData(session)

          // Update session status to mapping
          await updateImportSession(session.id, { status: 'mapping' })

        } catch (sessionError) {
          console.error('Failed to create import session:', sessionError)
          setError('Failed to create import session. Please try again.')
          setIsUploadingFile(false)
          return
        }

        // Check for matching template
        try {
          const template = await findMatchingTemplate(headers, user.id)
          if (template) {
            console.log('Matched template found:', template.name)
            setMatchedTemplate(template)
            setTemplateToApply(template)
            setShowTemplateDialog(true)
          }
        } catch (templateError) {
          console.error('Template detection error:', templateError)
          // Non-critical, continue without template
        }

        setCsvHeaders(headers)
        setCsvData(data)
        setCurrentStep('preview')
        setIsUploadingFile(false)
      } catch {
        setError('Failed to parse CSV file. Please check the file format.')
        setIsUploadingFile(false)
      }
    }

    reader.onerror = () => {
      setError('Failed to read the file')
      setIsUploadingFile(false)
    }

    reader.readAsText(file)
  }
}